// src/data/formulaData.js
// Comprehensive mathematical formulas catalog for CalcVoyager

const formulaData = {
  // ═══════════════════════════════════════════════════════════════════════════
  // 1. Calculus and Analytical Geometry
  // ═══════════════════════════════════════════════════════════════════════════
  "limits-continuity": {
    title: "Limits & Continuity",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      {
        name: "Epsilon-Delta Definition",
        formula: "∀ε > 0, ∃δ > 0 such that 0 < |x - a| < δ ⟹ |f(x) - L| < ε",
        note: "Rigorous definition of lim_(x→a) f(x) = L",
      },
      {
        name: "Squeeze (Sandwich) Theorem",
        formula: "If g(x) ≤ f(x) ≤ h(x) near a and lim g(x) = lim h(x) = L ⟹ lim f(x) = L",
        note: "Essential for bounding oscillatory functions like x² sin(1/x)",
      },
      {
        name: "Continuity Conditions at a Point",
        formula: "1) f(c) is defined; 2) lim_(x→c) f(x) exists; 3) lim_(x→c) f(x) = f(c)",
        note: "All three conditions must hold simultaneously",
      },
      {
        name: "Intermediate Value Theorem (IVT)",
        formula: "If f continuous on [a, b] and u between f(a) & f(b), ∃c ∈ (a, b) with f(c) = u",
        note: "Guarantees existence of roots when f(a) and f(b) have opposite signs",
      },
      {
        name: "L'Hôpital's Rule",
        formula: "lim_(x→a) [f(x)/g(x)] = lim_(x→a) [f'(x)/g'(x)]  (for 0/0 or ±∞/±∞)",
        note: "Differentiate numerator and denominator separately until determinate",
      },
      {
        name: "Special Trigonometric Limits",
        formula: "lim_(x→0) [sin(x)/x] = 1  and  lim_(x→0) [(1 - cos(x))/x] = 0",
        note: "Used to derive derivatives of trigonometric functions",
      },
    ],
  },

  differentiation: {
    title: "Differentiation Rules",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      {
        name: "Derivative Definition",
        formula: "f'(x) = lim_(h→0) [f(x+h) - f(x)] / h = df/dx",
        note: "Slope of the tangent line at x",
      },
      {
        name: "Power, Product & Quotient Rules",
        formula: "(xⁿ)' = n xⁿ⁻¹ · (uv)' = u'v + uv' · (u/v)' = (u'v - uv') / v²",
        note: "Foundation for all polynomial and algebraic derivatives",
      },
      {
        name: "Chain Rule",
        formula: "d/dx [f(g(x))] = f'(g(x)) · g'(x)",
        note: "Derivative of composite functions: outer derivative times inner derivative",
      },
      {
        name: "Exponential & Logarithmic Rules",
        formula: "d/dx [eˣ] = eˣ · d/dx [aˣ] = aˣ ln(a) · d/dx [ln(x)] = 1/x · d/dx [log_a(x)] = 1/(x ln a)",
        note: "ln(x) requires x > 0",
      },
      {
        name: "Trigonometric Derivatives",
        formula: "(sin x)' = cos x · (cos x)' = -sin x · (tan x)' = sec² x · (sec x)' = sec x tan x",
        note: "Cofunction derivatives always carry a negative sign",
      },
      {
        name: "Inverse Trigonometric Derivatives",
        formula: "d/dx [arcsin x] = 1/√(1-x²) · d/dx [arctan x] = 1/(1+x²)",
        note: "Critical for solving integration of rational forms",
      },
      {
        name: "Mean Value Theorem (MVT)",
        formula: "f'(c) = [f(b) - f(a)] / (b - a)  for some c ∈ (a, b)",
        note: "Requires continuity on [a, b] and differentiability on (a, b)",
      },
      {
        name: "Linearization (Tangent Line Approx)",
        formula: "L(x) = f(a) + f'(a)(x - a)",
        note: "First-order Taylor polynomial approximation near x = a",
      },
    ],
  },

  integration: {
    title: "Integration Techniques",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      {
        name: "Fundamental Theorem of Calculus (Part 1 & 2)",
        formula: "d/dx [∫_a^x f(t) dt] = f(x)  and  ∫_a^b f(x) dx = F(b) - F(a)  where F' = f",
        note: "Connects derivatives directly to definite area accumulation",
      },
      {
        name: "u-Substitution (Reverse Chain Rule)",
        formula: "∫ f(g(x)) g'(x) dx = ∫ f(u) du  where u = g(x), du = g'(x) dx",
        note: "Transform integrand into simpler standard integral form",
      },
      {
        name: "Integration by Parts",
        formula: "∫ u dv = u v - ∫ v du  (LIATE order for u: Log, Inv Trig, Alg, Trig, Exp)",
        note: "Used for products of algebraic and transcendental functions",
      },
      {
        name: "Trigonometric Substitution",
        formula: "√(a² - x²) ⟹ x = a sin θ · √(a² + x²) ⟹ x = a tan θ · √(x² - a²) ⟹ x = a sec θ",
        note: "Converts radical expressions into trigonometric identities",
      },
      {
        name: "Partial Fraction Decomposition",
        formula: "P(x)/[(x-r₁)(x-r₂)] = A/(x-r₁) + B/(x-r₂)",
        note: "For rational functions with degree of numerator < denominator",
      },
      {
        name: "Improper Integrals",
        formula: "∫_a^∞ f(x) dx = lim_(b→∞) ∫_a^b f(x) dx",
        note: "Converges if limit is finite; diverges if limit is ±∞ or does not exist",
      },
      {
        name: "Area & Arc Length",
        formula: "Area = ∫_a^b |f(x) - g(x)| dx · Arc Length L = ∫_a^b √(1 + [f'(x)]²) dx",
        note: "Area between curves and curve arc length in Cartesian coordinates",
      },
    ],
  },

  "sequences-series": {
    title: "Sequences & Series",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      {
        name: "Geometric Series Sum",
        formula: "Σ_{n=0}^∞ a rⁿ = a / (1 - r)  for |r| < 1  (diverges for |r| ≥ 1)",
        note: "First term divided by (1 - common ratio)",
      },
      {
        name: "nth-Term Divergence Test",
        formula: "If lim_(n→∞) a_n ≠ 0 (or DNE) ⟹ Σ a_n diverges",
        note: "If lim a_n = 0, the test is INCONCLUSIVE (e.g., harmonic series)",
      },
      {
        name: "p-Series Test",
        formula: "Σ_{n=1}^∞ 1/nᵖ converges if p > 1, diverges if p ≤ 1",
        note: "p = 1 is the divergent Harmonic Series Σ 1/n",
      },
      {
        name: "Integral Test",
        formula: "If f is positive, continuous & decreasing: Σ a_n & ∫_1^∞ f(x) dx both conv/div",
        note: "Use improper integral to determine infinite series behavior",
      },
      {
        name: "Ratio & Root Tests",
        formula: "L = lim |a_{n+1}/a_n| or L = lim |a_n|^(1/n): L < 1 (conv), L > 1 (div), L = 1 (inconcl)",
        note: "Ratio test is ideal for factorials n! and power terms cⁿ",
      },
      {
        name: "Alternating Series Test & Error Bound",
        formula: "If b_n > 0, b_{n+1} ≤ b_n, lim b_n = 0 ⟹ Σ (-1)ⁿ b_n converges; |R_n| ≤ b_{n+1}",
        note: "Truncation error is bounded by the magnitude of the first omitted term",
      },
      {
        name: "Power Series & Radius of Convergence",
        formula: "Σ c_n (x - a)ⁿ converges for |x - a| < R, where R = 1 / lim |c_{n+1}/c_n|",
        note: "Check endpoints x = a ± R individually for full interval of convergence",
      },
    ],
  },

  "conic-sections": {
    title: "Conic Sections & Geometry",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      {
        name: "Standard Parabola",
        formula: "(y - k)² = 4p(x - h)  [horizontal]  or  (x - h)² = 4p(y - k)  [vertical]",
        note: "Focus at (h+p, k) or (h, k+p); Directrix: x = h-p or y = k-p",
      },
      {
        name: "Standard Ellipse",
        formula: "(x - h)² / a² + (y - k)² / b² = 1  with c² = a² - b² (a > b)",
        note: "Foci at (h ± c, k); Eccentricity e = c/a < 1",
      },
      {
        name: "Standard Hyperbola",
        formula: "(x - h)² / a² - (y - k)² / b² = 1  with c² = a² + b²",
        note: "Foci at (h ± c, k); Asymptotes: y - k = ±(b/a)(x - h); Eccentricity e = c/a > 1",
      },
      {
        name: "General Conic Discriminant",
        formula: "Ax² + Bxy + Cy² + Dx + Ey + F = 0 ⟹ Discriminant Δ = B² - 4AC",
        note: "Δ < 0: Ellipse/Circle; Δ = 0: Parabola; Δ > 0: Hyperbola",
      },
      {
        name: "Point-to-Line Distance",
        formula: "d = |Ax₀ + By₀ + C| / √(A² + B²)",
        note: "Perpendicular distance from point (x₀, y₀) to line Ax + By + C = 0",
      },
    ],
  },

  "taylor-series": {
    title: "Taylor & Maclaurin Series",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      {
        name: "Taylor Polynomial Expansion",
        formula: "P_n(x) = Σ_{k=0}^n [f⁽ᵏ⁾(a) / k!] (x - a)ᵏ",
        note: "Local polynomial approximation of degree n centered at x = a",
      },
      {
        name: "Maclaurin Series (Centered at 0)",
        formula: "f(x) = Σ_{k=0}^∞ [f⁽ᵏ⁾(0) / k!] xᵏ",
        note: "Special case of Taylor series with center a = 0",
      },
      {
        name: "Lagrange Error Remainder",
        formula: "R_n(x) = [f⁽ⁿ⁺¹⁾(c) / (n+1)!] (x - a)ⁿ⁺¹  for some c between a and x",
        note: "Gives strict upper bound on polynomial approximation error",
      },
      {
        name: "Catalog: eˣ, sin(x), cos(x)",
        formula: "eˣ = Σ xⁿ/n! · sin x = Σ (-1)ⁿ x²ⁿ⁺¹/(2n+1)! · cos x = Σ (-1)ⁿ x²ⁿ/(2n)!",
        note: "All three series converge for all x ∈ (-∞, ∞)",
      },
      {
        name: "Catalog: 1/(1-x) & ln(1+x)",
        formula: "1/(1-x) = Σ xⁿ (|x| < 1) · ln(1+x) = Σ (-1)ⁿ⁺¹ xⁿ/n (-1 < x ≤ 1)",
        note: "Geometric series generator and its term-by-term integral",
      },
    ],
  },

  "lines-geometry": {
    title: "2D Lines & Systems of Lines",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      {
        name: "Section Formula (Internal & External)",
        formula: "Internal: P = ((k₁x₂ + k₂x₁)/(k₁ + k₂), (k₁y₂ + k₂y₁)/(k₁ + k₂)) · External: Q = ((k₁x₂ - k₂x₁)/(k₁ - k₂), (k₁y₂ - k₂y₁)/(k₁ - k₂))",
        note: "Divides line segment joining (x₁, y₁) and (x₂, y₂) in ratio k₁ : k₂",
      },
      {
        name: "Triangle Centers",
        formula: "Centroid G = ((Σxᵢ)/3, (Σyᵢ)/3) · Incenter I = ((ax₁ + bx₂ + cx₃)/(a+b+c), (ay₁ + by₂ + cy₃)/(a+b+c))",
        note: "Euler Line: Centroid G divides segment HO (Orthocenter to Circumcenter) in ratio 2 : 1",
      },
      {
        name: "Six Standard Forms of a Line",
        formula: "Slope-Int: y = mx + c · Pt-Slope: y - y₁ = m(x - x₁) · 2-Pt: (y - y₁)/(y₂ - y₁) = (x - x₁)/(x₂ - x₁) · Int: x/a + y/b = 1 · Normal: x cos α + y sin α = p · Parametric: (x - x₁)/cos θ = (y - y₁)/sin θ = r",
        note: "Complete representation across Cartesian, intercept, normal and distance forms",
      },
      {
        name: "Perpendicular Distance & Parallel Separation",
        formula: "d(pt, line) = |Ax₀ + By₀ + C| / √(A² + B²) · d(parallel lines) = |C₁ - C₂| / √(A² + B²)",
        note: "Both parallel lines must be written with identical coefficients A and B",
      },
      {
        name: "Angle Between Two Lines",
        formula: "tan θ = |(m₂ - m₁) / (1 + m₁m₂)| · Parallel iff m₁ = m₂ · Perpendicular iff m₁m₂ = -1 (A₁A₂ + B₁B₂ = 0)",
        note: "Computes the acute angle between intersecting lines",
      },
      {
        name: "Condition of Concurrency of Three Lines",
        formula: "det [ [A₁, B₁, C₁], [A₂, B₂, C₂], [A₃, B₃, C₃] ] = 0",
        note: "Three lines intersect at a unique common point if and only if the coefficient determinant is zero",
      },
      {
        name: "Homogeneous Pair of Straight Lines",
        formula: "ax² + 2hxy + by² = 0 · Angle: tan θ = [2√(h² - ab)] / (a + b) · Perpendicular: a + b = 0 · Coincident: h² = ab",
        note: "Represents two lines passing through the origin (0, 0)",
      },
      {
        name: "General Second-Degree Pair of Lines",
        formula: "ax² + 2hxy + by² + 2gx + 2fy + c = 0 represents lines iff Δ = abc + 2fgh - af² - bg² - ch² = 0",
        note: "Intersection point is found by simultaneous solution of ∂F/∂x = 0 and ∂F/∂y = 0",
      },
      {
        name: "Joint Equation of Angle Bisectors",
        formula: "(x² - y²) / (a - b) = xy / h  or  h(x² - y²) = (a - b)xy",
        note: "The pair of angle bisectors is always mutually perpendicular (coefficients of x² and y² sum to zero)",
      },
    ],
  },

  "circles-tangents": {
    title: "Circles & Conic Tangents/Normals",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      {
        name: "Standard & General Circle Equations",
        formula: "Standard: (x - h)² + (y - k)² = r² · General: x² + y² + 2gx + 2fy + c = 0, Center (-g, -f), r = √(g² + f² - c)",
        note: "Real circle requires g² + f² - c > 0; Point circle if = 0; Imaginary circle if < 0",
      },
      {
        name: "Circle Through 3 Points & Diameter Form",
        formula: "Diameter Form: (x - x₁)(x - x₂) + (y - y₁)(y - y₂) = 0",
        note: "Subtends a 90° angle at every point on the circumference",
      },
      {
        name: "Parametric Form of Circle",
        formula: "x = h + r cos θ,  y = k + r sin θ  (0 ≤ θ < 2π)",
        note: "Trigonometric representation with center (h, k) and radius r",
      },
      {
        name: "Tangent at a Point via T = 0 Substitution Rule",
        formula: "x² → xx₁,  y² → yy₁,  xy → (xy₁ + x₁y)/2,  x → (x + x₁)/2,  y → (y + y₁)/2",
        note: "Universal tangent rule for any second-degree conic at point (x₁, y₁) lying on the curve",
      },
      {
        name: "Conditions of Tangency (y = mx + c)",
        formula: "Circle: c² = r²(1 + m²) · Parabola (y² = 4ax): c = a/m · Ellipse: c² = a²m² + b² · Hyperbola: c² = a²m² - b²",
        note: "Determines the exact c value for a line of slope m to touch each standard conic",
      },
      {
        name: "Length of Tangent from External Point",
        formula: "L = √(x₁² + y₁² + 2gx₁ + 2fy₁ + c) = √S₁",
        note: "Valid for points outside the circle (S₁ > 0)",
      },
      {
        name: "Director Circles (Perpendicular Tangents Locus)",
        formula: "Circle: x² + y² = 2r² · Ellipse: x² + y² = a² + b² · Hyperbola: x² + y² = a² - b² · Parabola: Directrix x = -a",
        note: "Locus of intersection points of mutually perpendicular tangents",
      },
      {
        name: "Chord of Contact of Tangents",
        formula: "T = 0 ⟹ xx₁ + yy₁ + g(x + x₁) + f(y + y₁) + c = 0",
        note: "Line connecting the two points of tangency from an external point (x₁, y₁)",
      },
    ],
  },

  "advanced-calculus": {
    title: "Advanced Single-Variable Calculus",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      {
        name: "Formal Epsilon-Delta Limit Definition",
        formula: "∀ε > 0, ∃δ > 0 such that 0 < |x - c| < δ ⟹ |f(x) - L| < ε",
        note: "Rigorous foundation for single-variable limits and analysis proofs",
      },
      {
        name: "Classification of Discontinuities",
        formula: "Removable (lim exists ≠ f(c)) · Jump (lim_left ≠ lim_right finite) · Infinite (lim = ±∞) · Essential/Oscillating (no limit)",
        note: "Four canonical types of mathematical discontinuity",
      },
      {
        name: "IVT, EVT & Rolle's Theorem",
        formula: "IVT: f(c) = u for continuous f on [a,b] · Rolle's: f'(c) = 0 if f(a) = f(b) · Cauchy MVT: f'(c)/g'(c) = [f(b)-f(a)]/[g(b)-g(a)]",
        note: "Cornerstone existence theorems of real analysis",
      },
      {
        name: "Logarithmic & Parametric Differentiation",
        formula: "Log: y = f(x)ᵍ⁽ˣ⁾ ⟹ y' = y [g'(x) ln f(x) + g(x) f'(x)/f(x)] · Parametric: dy/dx = y'(t)/x'(t) · d²y/dx² = [d/dt(dy/dx)] / x'(t)",
        note: "Logarithmic differentiation handles variable towers; parametric chain rule avoids x-elimination",
      },
      {
        name: "Hyperbolic Functions & Derivatives",
        formula: "cosh² x - sinh² x = 1 · d/dx[sinh x] = cosh x · d/dx[cosh x] = sinh x · d/dx[tanh x] = sech² x · d/dx[arcsinh x] = 1/√(1 + x²)",
        note: "Hyperbolic cosine derivative carries positive sign (unlike circular cosine)",
      },
      {
        name: "Extended L'Hôpital's Rule (All Indeterminate Forms)",
        formula: "0/0,  ∞/∞,  0 · ∞,  ∞ - ∞,  1^∞,  0⁰,  ∞⁰",
        note: "Exponentials 1^∞, 0⁰, ∞⁰ are converted to 0/0 or ∞/∞ via natural logarithm transform ln L",
      },
      {
        name: "Curvature & Radius of Curvature",
        formula: "Explicit: κ(x) = |y''| / (1 + (y')²)^(3/2) · Parametric: κ(t) = |x'y'' - y'x''| / (x'² + y'²)^(3/2) · Radius ρ = 1/κ",
        note: "Evolute center: α = x - y'(1 + (y')²)/y'', β = y + (1 + (y')²)/y''",
      },
      {
        name: "Arc Length & Surface of Revolution",
        formula: "Arc Length: s = ∫ₐᵇ √(1 + (f'(x))²) dx · Surface Area (x-axis): S = 2π ∫ₐᵇ y √(1 + (y')²) dx",
        note: "Differential arc element ds = √(dx² + dy²)",
      },
      {
        name: "Physical Applications of Integration",
        formula: "Work: W = ∫ F(x) dx · Centroid: x̄ = (1/A)∫ x f(x) dx, ȳ = (1/2A)∫ [f(x)]² dx · Hydrostatic: F = ∫ ρ g h(y) w(y) dy",
        note: "Applied single-variable mechanics, centroids of laminas, and fluid pressure forces",
      },
    ],
  },

  "differential-equations": {
    title: "Ordinary Differential Equations (ODEs)",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      {
        name: "Separable Differential Equations",
        formula: "dy/dx = g(x) h(y) ⟹ ∫ [1/h(y)] dy = ∫ g(x) dx + C",
        note: "Separates dependent and independent variables directly into two single integrals",
      },
      {
        name: "Homogeneous First-Order ODEs",
        formula: "dy/dx = F(y/x) ⟹ substitute y = vx,  dy/dx = v + x (dv/dx) ⟹ dv / [F(v) - v] = dx / x",
        note: "Converts degree-homogeneous first-order ODEs into separable form in variable v",
      },
      {
        name: "First-Order Linear ODEs (Integrating Factor)",
        formula: "dy/dx + P(x)y = Q(x) ⟹ Integrating Factor I(x) = e^(∫ P(x) dx) ⟹ y(x) = [1/I(x)] [∫ I(x) Q(x) dx + C]",
        note: "Multiplies through by I(x) to condense LHS into exact derivative d/dx[I(x) y]",
      },
      {
        name: "Exact Differential Equations",
        formula: "M(x, y) dx + N(x, y) dy = 0 is exact iff ∂M/∂y = ∂N/∂x ⟹ Potential function Ψ(x, y) = C",
        note: "Integrate M with respect to x, then determine g(y) by matching ∂Ψ/∂y = N",
      },
      {
        name: "Bernoulli Equation Transformation",
        formula: "dy/dx + P(x)y = Q(x)yⁿ ⟹ divide by yⁿ, substitute u = y^(1-n) ⟹ du/dx + (1-n)P(x)u = (1-n)Q(x)",
        note: "Converts non-linear power equations into standard first-order linear ODEs in u",
      },
      {
        name: "2nd-Order Linear Homogeneous ODEs",
        formula: "ay'' + by' + cy = 0 · Char: ar² + br + c = 0 ⟹ Distinct: y = c₁e^(r₁x) + c₂e^(r₂x) · Repeated: y = (c₁ + c₂x)e^(rx) · Complex (α ± iβ): y = e^(αx)[c₁ cos(βx) + c₂ sin(βx)]",
        note: "Classification according to discriminant b² - 4ac",
      },
      {
        name: "Method of Undetermined Coefficients",
        formula: "y(x) = y_c(x) + y_p(x) · Polynomial: A_n xⁿ + ... · Exponential: A e^(kx) · Sinusoidal: A cos(ωx) + B sin(ωx)",
        note: "Multiply trial form by xˢ if forcing term duplicates a complementary homogeneous solution",
      },
      {
        name: "Variation of Parameters & Wronskian",
        formula: "W(y₁, y₂) = y₁y₂' - y₁'y₂ · y_p = -y₁ ∫ [y₂ g(x) / W] dx + y₂ ∫ [y₁ g(x) / W] dx",
        note: "Universal particular solution method for any forcing term g(x), including tan x, sec x, 1/x",
      },
    ],
  },

  "pointwise-uniform-convergence": {
    title: "Pointwise & Uniform Convergence",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      {
        name: "Pointwise Convergence Definition",
        formula: "f_n → f pointwise on S ⟺ ∀x ∈ S, ∀ε > 0, ∃N(ε, x) such that n > N ⟹ |f_n(x) - f(x)| < ε",
        note: "Limit index N may vary with location x",
      },
      {
        name: "Uniform Convergence (Supremum Norm)",
        formula: "f_n → f uniformly on S ⟺ lim_(n→∞) ||f_n - f||_∞ = lim_(n→∞) sup_(x∈S) |f_n(x) - f(x)| = 0",
        note: "Global convergence across the entire set S at a uniform rate",
      },
      {
        name: "The Uniform Limit Theorem",
        formula: "If each f_n is continuous on S and f_n → f uniformly on S ⟹ limit function f is continuous on S",
        note: "Preservation of continuity under uniform convergence (3ε-proof)",
      },
      {
        name: "Weierstrass M-Test for Series",
        formula: "If |u_n(x)| ≤ M_n on S and ∑ M_n < ∞ ⟹ ∑ u_n(x) converges uniformly and absolutely on S",
        note: "Master test for proving uniform convergence of function series",
      },
      {
        name: "Cauchy Criterion for Uniform Convergence",
        formula: "f_n → f uniformly on S ⟺ ∀ε > 0, ∃N such that m, n > N ⟹ |f_n(x) - f_m(x)| < ε  ∀x ∈ S",
        note: "Intrinsic convergence check without explicit reference to the limit function",
      },
    ],
  },

  "interchanging-limits": {
    title: "Interchanging Limits, Derivatives & Integrals",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      {
        name: "Termwise Integration Theorem",
        formula: "If f_n → f uniformly on [a, b] ⟹ lim_(n→∞) ∫_a^b f_n(x) dx = ∫_a^b [lim_(n→∞) f_n(x)] dx = ∫_a^b f(x) dx",
        note: "Uniform convergence justifies switching integral and limit signs",
      },
      {
        name: "Termwise Differentiation Theorem",
        formula: "If f_n(x_0) converges and f_n' → g uniformly on (a, b) ⟹ f_n → f uniformly and f'(x) = lim f_n'(x) = g(x)",
        note: "Crucial: uniform convergence of derivatives f_n', not original f_n, is required",
      },
      {
        name: "Bounded Convergence Theorem",
        formula: "If |f_n(x)| ≤ M on [a, b] and f_n → f pointwise ⟹ lim_(n→∞) ∫_a^b f_n(x) dx = ∫_a^b f(x) dx",
        note: "Arzelà-Osgood theorem: bounded pointwise convergence suffices for Riemann integrals",
      },
      {
        name: "Dominated Convergence (DCT)",
        formula: "If |f_n(x)| ≤ g(x) on E with ∫_E g dx < ∞ and f_n → f pointwise ⟹ lim ∫_E f_n dx = ∫_E f dx",
        note: "Lebesgue dominated convergence theorem with integrable dominating envelope g(x)",
      },
    ],
  },

  "power-series-ode": {
    title: "Power-Series Solutions of ODEs",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      {
        name: "Ordinary Point Criterion",
        formula: "In y'' + P(x)y' + Q(x)y = 0, x_0 is an ordinary point iff P(x) and Q(x) are analytic at x_0",
        note: "Taylor series expansions of coefficients have positive radii of convergence",
      },
      {
        name: "Power Series Ansatz & Derivatives",
        formula: "y(x) = ∑_(n=0)^∞ a_n (x - x_0)ⁿ · y' = ∑_(n=1)^∞ n a_n (x - x_0)ⁿ⁻¹ · y'' = ∑_(n=2)^∞ n(n-1)a_n (x - x_0)ⁿ⁻²",
        note: "Substituted into ODE to align powers of (x - x_0)",
      },
      {
        name: "Index Shift for Second Derivatives",
        formula: "∑_(n=2)^∞ n(n-1) a_n xⁿ⁻² = ∑_(k=0)^∞ (k+2)(k+1) a_(k+2) x^k",
        note: "Re-indexing shifts all terms to common dummy power x^k",
      },
      {
        name: "Fundamental Solution Basis",
        formula: "y(x) = a_0 y_1(x) + a_1 y_2(x) with y_1(0)=1, y_1'(0)=0 and y_2(0)=0, y_2'(0)=1",
        note: "a_0 and a_1 serve as the two independent arbitrary constants",
      },
      {
        name: "Fuchs Lower Bound on Convergence Radius",
        formula: "R ≥ min { |x_0 - z_k| : z_k is a complex singular point of P(x) or Q(x) }",
        note: "Guarantees minimum domain of validity for truncated power-series approximations",
      },
    ],
  },

  "frobenius-method": {
    title: "Frobenius Method (Singular ODEs)",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      {
        name: "Regular Singular Point Test",
        formula: "x=0 is regular singular for y'' + P(x)y' + Q(x)y = 0 iff x P(x) and x² Q(x) are analytic at 0",
        note: "Singularity has at most a first-order pole in P and second-order pole in Q",
      },
      {
        name: "Frobenius Generalized Series Ansatz",
        formula: "y(x) = x^r ∑_(n=0)^∞ a_n xⁿ = ∑_(n=0)^∞ a_n x^(n+r)  with a_0 ≠ 0",
        note: "Index r is determined by the lowest-power coefficient balance",
      },
      {
        name: "Indicial Equation",
        formula: "F(r) = r(r - 1) + p_0 r + q_0 = 0,  where p_0 = lim_(x→0) x P(x), q_0 = lim_(x→0) x² Q(x)",
        note: "Quadratic equation whose roots r_1, r_2 govern solution forms",
      },
      {
        name: "Distinct Roots (Non-Integer Difference)",
        formula: "r_1 - r_2 ∉ ℤ ⟹ y_1(x) = x^(r_1) ∑ a_n xⁿ  and  y_2(x) = x^(r_2) ∑ b_n xⁿ",
        note: "Yields two linearly independent Frobenius power series directly",
      },
      {
        name: "Repeated Indicial Roots Case",
        formula: "r_1 = r_2 = r ⟹ y_1(x) = x^r ∑ a_n xⁿ  and  y_2(x) = y_1(x) ln(x) + x^r ∑ c_n xⁿ",
        note: "Second solution arises from derivative ∂y(x,r)/∂r and contains a logarithmic branch",
      },
    ],
  },

  "analytic-functions": {
    title: "Analytic Functions & Cauchy-Riemann",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      {
        name: "Complex Derivative Definition",
        formula: "f'(z_0) = lim_(Δz→0) [f(z_0 + Δz) - f(z_0)] / Δz",
        note: "Limit must be independent of path direction in the complex plane",
      },
      {
        name: "Cartesian Cauchy-Riemann Equations",
        formula: "∂u/∂x = ∂v/∂y   and   ∂u/∂y = -∂v/∂x   (with continuous first partials)",
        note: "Necessary and sufficient conditions for complex differentiability of f = u + iv",
      },
      {
        name: "Polar Cauchy-Riemann Equations",
        formula: "∂u/∂r = (1/r) ∂v/∂θ   and   ∂v/∂r = -(1/r) ∂u/∂θ",
        note: "Polar form for z = r e^(iθ) with derivative f'(z) = e^(-iθ)(u_r + i v_r)",
      },
      {
        name: "Harmonic Potential & Laplace Equation",
        formula: "∇²u = ∂²u/∂x² + ∂²u/∂y² = 0   and   ∇²v = 0",
        note: "Real and imaginary parts of holomorphic functions are automatically harmonic",
      },
      {
        name: "Milne-Thomson Reconstruction Method",
        formula: "f'(z) = u_x(z, 0) - i u_y(z, 0) ⟹ f(z) = ∫ [u_x(z, 0) - i u_y(z, 0)] dz + C",
        note: "Constructs entire analytic f(z) directly from u(x, y) without integrating v(x, y)",
      },
      {
        name: "Orthogonality of Level Trajectories",
        formula: "∇u · ∇v = u_x v_x + u_y v_y = u_x(-u_y) + u_y(u_x) = 0",
        note: "Equipotential curves u(x, y) = c_1 and streamlines v(x, y) = c_2 intersect at 90°",
      },
    ],
  },

  "contour-integrals": {
    title: "Contour Integrals & Residues",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      {
        name: "Parametric Complex Line Integral",
        formula: "∫_γ f(z) dz = ∫_a^b f(γ(t)) γ'(t) dt = ∫_γ (u dx - v dy) + i ∫_γ (v dx + u dy)",
        note: "Reduction of complex contour integral to real parametric line integrals",
      },
      {
        name: "The ML-Inequality (Darboux Bound)",
        formula: "|∫_γ f(z) dz| ≤ M · L,   where M = max_(z∈γ) |f(z)|  and  L = length(γ)",
        note: "Fundamental inequality for proving vanishing integrals on large semicircular arcs",
      },
      {
        name: "Cauchy-Goursat Theorem",
        formula: "If f is analytic in simply connected D, then ∮_γ f(z) dz = 0 for every closed contour γ",
        note: "Vanishing of closed loop integrals in holomorphic domains",
      },
      {
        name: "Cauchy's Integral Formula (CIF)",
        formula: "f(z_0) = (1 / 2πi) ∮_γ [f(z) / (z - z_0)] dz",
        note: "Values on the boundary contour uniquely dictate interior function values",
      },
      {
        name: "Higher Derivative CIF",
        formula: "f^(n)(z_0) = (n! / 2πi) ∮_γ [f(z) / (z - z_0)^(n+1)] dz ⟹ ∮_γ [f(z)/(z - z_0)^(n+1)] dz = (2πi/n!) f^(n)(z_0)",
        note: "Holomorphic functions possess derivatives of all orders",
      },
      {
        name: "Cauchy's Residue Theorem",
        formula: "∮_γ f(z) dz = 2πi ∑_(k=1)^N Res(f, z_k)",
        note: "Integrals around isolated singularities equal 2πi times the sum of enclosed residues",
      },
      {
        name: "Residue Calculation Formulas",
        formula: "Simple pole: Res(f, z_0) = lim_(z→z_0) (z - z_0)f(z) · Order m: Res = (1/(m-1)!) lim d^(m-1)/dz^(m-1) [(z - z_0)ᵐ f(z)]",
        note: "Quotient rule for P(z)/Q(z): Res(z_0) = P(z_0) / Q'(z_0) when Q'(z_0) ≠ 0",
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Calculus and Analytical Geometry — further modules
  // ═══════════════════════════════════════════════════════════════════════════
  "space-curves": {
    title: "Space Curves (Frenet-Serret)",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      { name: "Unit Tangent", formula: "T = r′(t) / |r′(t)|", note: "Direction of motion along the curve" },
      { name: "Arc Length", formula: "s = ∫ₐᵇ |r′(t)| dt", note: "Parametrize by arc length to get unit speed" },
      { name: "Curvature", formula: "κ = |r′ × r″| / |r′|³ = |dT/ds|", note: "Circle of radius R has κ = 1/R" },
      { name: "Torsion", formula: "τ = (r′ × r″) · r‴ / |r′ × r″|²", note: "τ = 0 for every planar curve" },
      { name: "Frenet-Serret Equations", formula: "T′ = κN,  N′ = −κT + τB,  B′ = −τN  (′ = d/ds)", note: "B = T × N completes the moving frame" },
    ],
  },
  "vector-motion": {
    title: "Vector-Valued Functions & Motion",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      { name: "Velocity & Acceleration", formula: "v = r′(t),  a = r″(t),  speed = |v|", note: "Differentiate each component separately" },
      { name: "Tangential / Normal Split", formula: "a = (d|v|/dt) T + κ|v|² N", note: "aₜ = (v·a)/|v|,  aₙ = |v × a|/|v|" },
      { name: "Projectile Motion", formula: "r(t) = ⟨v₀cos α · t, h + v₀sin α · t − ½gt²⟩", note: "Range on level ground: v₀² sin 2α / g" },
      { name: "Integrating Back", formula: "v(t) = v(0) + ∫₀ᵗ a dτ,  r(t) = r(0) + ∫₀ᵗ v dτ", note: "Initial conditions fix the constants" },
    ],
  },
  "parametric-surfaces": {
    title: "Parametric Surfaces",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      { name: "Parametrization", formula: "r(u, v) = ⟨x(u, v), y(u, v), z(u, v)⟩", note: "Two parameters trace out a surface" },
      { name: "Normal Vector", formula: "n = r_u × r_v", note: "Tangent plane: n · (r − r₀) = 0" },
      { name: "Surface Area", formula: "A = ∬_D |r_u × r_v| du dv", note: "For z = f(x, y): ∬ √(1 + fₓ² + f_y²) dA" },
      { name: "Sphere", formula: "r(φ, θ) = ⟨a sin φ cos θ, a sin φ sin θ, a cos φ⟩,  |r_φ × r_θ| = a² sin φ", note: "Surface area 4πa²" },
    ],
  },
  "polar-calculus": {
    title: "Polar Coordinate Calculus",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      { name: "Conversion", formula: "x = r cos θ,  y = r sin θ,  r² = x² + y²", note: "Choose θ by the quadrant of (x, y)" },
      { name: "Slope of r = f(θ)", formula: "dy/dx = (r′ sin θ + r cos θ) / (r′ cos θ − r sin θ)", note: "Horizontal tangents where the numerator is 0" },
      { name: "Area", formula: "A = ½ ∫_α^β r² dθ", note: "Between curves: ½ ∫ (r_out² − r_in²) dθ" },
      { name: "Arc Length", formula: "L = ∫_α^β √(r² + (dr/dθ)²) dθ", note: "Cardioid r = 1 + cos θ has length 8" },
    ],
  },
  "solids-revolution": {
    title: "Solids of Revolution",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      { name: "Disc Method", formula: "V = π ∫ₐᵇ [R(x)]² dx", note: "Slices perpendicular to the axis" },
      { name: "Washer Method", formula: "V = π ∫ₐᵇ ([R(x)]² − [r(x)]²) dx", note: "Subtract the hole" },
      { name: "Shell Method", formula: "V = 2π ∫ₐᵇ (radius)(height) dx", note: "Slices parallel to the axis" },
      { name: "Surface Area of Revolution", formula: "S = 2π ∫ₐᵇ f(x) √(1 + [f′(x)]²) dx", note: "Rotating y = f(x) about the x-axis" },
      { name: "Pappus's Theorem", formula: "V = 2π ȳ A", note: "ȳ = distance from the centroid to the axis" },
    ],
  },
  "volume-cross-sections": {
    title: "Volume by Cross-Sections",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      { name: "Slicing Formula", formula: "V = ∫ₐᵇ A(x) dx", note: "A(x) = area of the slice at x" },
      { name: "Square Slices", formula: "A(x) = s(x)²", note: "s = side length across the base" },
      { name: "Equilateral Triangle Slices", formula: "A(x) = (√3/4) s(x)²", note: "s = side across the base" },
      { name: "Semicircle Slices", formula: "A(x) = (π/8) d(x)²", note: "d = diameter across the base" },
      { name: "Cavalieri's Principle", formula: "A₁(x) = A₂(x) for all x ⟹ V₁ = V₂", note: "Equal slices give equal volumes" },
    ],
  },
  "numerical-methods": {
    title: "Numerical Methods",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      { name: "Newton's Method", formula: "xₙ₊₁ = xₙ − f(xₙ) / f′(xₙ)", note: "Quadratic convergence near a simple root" },
      { name: "Bisection", formula: "c = (a + b)/2; keep the half where f changes sign", note: "Error after n steps ≤ (b − a)/2ⁿ" },
      { name: "Trapezoidal Rule", formula: "∫ₐᵇ f ≈ (h/2)[f₀ + 2f₁ + … + 2fₙ₋₁ + fₙ]", note: "Error ≤ (b − a)h² max|f″| / 12" },
      { name: "Simpson's Rule", formula: "∫ₐᵇ f ≈ (h/3)[f₀ + 4f₁ + 2f₂ + … + 4fₙ₋₁ + fₙ]", note: "n even; error ≤ (b − a)h⁴ max|f⁽⁴⁾| / 180" },
      { name: "Euler's Method", formula: "yₙ₊₁ = yₙ + h f(xₙ, yₙ)", note: "First-order: halving h roughly halves the error" },
    ],
  },
  "improper-integrals-advanced": {
    title: "Improper Integrals — Advanced Tests",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      { name: "Definition", formula: "∫ₐ^∞ f dx = lim_(b→∞) ∫ₐᵇ f dx", note: "Converges only if the limit is finite" },
      { name: "p-Integrals", formula: "∫₁^∞ dx/xᵖ < ∞ ⟺ p > 1;  ∫₀¹ dx/xᵖ < ∞ ⟺ p < 1", note: "The two benchmark families" },
      { name: "Direct Comparison", formula: "0 ≤ f ≤ g: ∫g converges ⟹ ∫f converges", note: "And ∫f diverges ⟹ ∫g diverges" },
      { name: "Limit Comparison", formula: "lim f/g = L ∈ (0, ∞) ⟹ both converge or both diverge", note: "Compare with the dominant power" },
      { name: "Absolute Convergence", formula: "∫|f| < ∞ ⟹ ∫f converges", note: "∫₁^∞ sin x / x converges, but not absolutely" },
    ],
  },
  "complex-numbers": {
    title: "Complex Numbers & De Moivre",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      { name: "Modulus & Argument", formula: "z = x + iy,  |z| = √(x² + y²),  arg z = atan2(y, x)", note: "z z̄ = |z|²" },
      { name: "Euler's Formula", formula: "e^(iθ) = cos θ + i sin θ", note: "z = r e^(iθ) is the polar form" },
      { name: "Multiplication", formula: "r₁e^(iθ₁) · r₂e^(iθ₂) = r₁r₂ e^(i(θ₁+θ₂))", note: "Multiply moduli, add arguments" },
      { name: "De Moivre's Theorem", formula: "(cos θ + i sin θ)ⁿ = cos nθ + i sin nθ", note: "Expands cos nθ and sin nθ" },
      { name: "nth Roots", formula: "z^(1/n) = r^(1/n) e^(i(θ + 2πk)/n),  k = 0, …, n − 1", note: "n roots equally spaced on a circle" },
    ],
  },
  "hyperbolic-functions": {
    title: "Hyperbolic Functions & Inverses",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      { name: "Definitions", formula: "sinh x = (eˣ − e⁻ˣ)/2,  cosh x = (eˣ + e⁻ˣ)/2,  tanh x = sinh x / cosh x", note: "cosh is even, sinh is odd" },
      { name: "Fundamental Identity", formula: "cosh²x − sinh²x = 1", note: "Compare cos² + sin² = 1" },
      { name: "Derivatives", formula: "(sinh x)′ = cosh x,  (cosh x)′ = sinh x,  (tanh x)′ = sech²x", note: "No minus sign for cosh" },
      { name: "Inverses", formula: "sinh⁻¹x = ln(x + √(x² + 1)),  cosh⁻¹x = ln(x + √(x² − 1))", note: "tanh⁻¹x = ½ ln((1 + x)/(1 − x)), |x| < 1" },
      { name: "Useful Integrals", formula: "∫ dx/√(x² + a²) = sinh⁻¹(x/a) + C", note: "∫ dx/√(x² − a²) = cosh⁻¹(x/a) + C" },
    ],
  },
  "laplace-transforms": {
    title: "Laplace Transforms",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      { name: "Definition", formula: "F(s) = ∫₀^∞ e^(−st) f(t) dt", note: "Exists for s beyond the growth rate of f" },
      { name: "Basic Pairs", formula: "L{1} = 1/s,  L{tⁿ} = n!/sⁿ⁺¹,  L{e^(at)} = 1/(s − a)", note: "L{sin bt} = b/(s² + b²),  L{cos bt} = s/(s² + b²)" },
      { name: "Derivatives", formula: "L{f′} = sF − f(0),  L{f″} = s²F − s f(0) − f′(0)", note: "Turns ODEs into algebra" },
      { name: "Shifting Theorems", formula: "L{e^(at)f(t)} = F(s − a),  L{u(t − c) f(t − c)} = e^(−cs)F(s)", note: "s-shift and t-shift" },
      { name: "Convolution", formula: "L{(f * g)(t)} = F(s) G(s),  (f * g)(t) = ∫₀ᵗ f(τ) g(t − τ) dτ", note: "Products in s are convolutions in t" },
    ],
  },
  "fourier-series": {
    title: "Fourier Series",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      { name: "Series (period 2L)", formula: "f(x) = a₀/2 + Σ [aₙ cos(nπx/L) + bₙ sin(nπx/L)]", note: "Valid where f is piecewise smooth" },
      { name: "Coefficients", formula: "aₙ = (1/L) ∫₋ₗᴸ f cos(nπx/L) dx,  bₙ = (1/L) ∫₋ₗᴸ f sin(nπx/L) dx", note: "a₀ uses n = 0" },
      { name: "Even / Odd Shortcuts", formula: "f even ⟹ bₙ = 0;  f odd ⟹ aₙ = 0", note: "Cosine series and sine series" },
      { name: "Dirichlet Convergence", formula: "Series → [f(x⁻) + f(x⁺)] / 2", note: "Midpoint of any jump" },
      { name: "Parseval's Identity", formula: "(1/L) ∫₋ₗᴸ f² dx = a₀²/2 + Σ (aₙ² + bₙ²)", note: "Energy is shared among the harmonics" },
    ],
  },
  "gamma-beta-functions": {
    title: "Gamma & Beta Functions",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      { name: "Gamma Function", formula: "Γ(x) = ∫₀^∞ t^(x−1) e^(−t) dt,  x > 0", note: "Γ(n + 1) = n! for integers n ≥ 0" },
      { name: "Recursion & Half Value", formula: "Γ(x + 1) = x Γ(x),  Γ(½) = √π", note: "Γ(3/2) = √π / 2" },
      { name: "Beta Function", formula: "B(a, b) = ∫₀¹ t^(a−1) (1 − t)^(b−1) dt = Γ(a)Γ(b) / Γ(a + b)", note: "Symmetric: B(a, b) = B(b, a)" },
      { name: "Trigonometric Form", formula: "∫₀^(π/2) sin^(2a−1)θ cos^(2b−1)θ dθ = ½ B(a, b)", note: "Evaluates Wallis-type integrals" },
      { name: "Reflection Formula", formula: "Γ(x) Γ(1 − x) = π / sin(πx)", note: "Valid for non-integer x" },
    ],
  },
  "leibniz-rule": {
    title: "Differentiation Under the Integral Sign",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      { name: "Constant Limits", formula: "d/dx ∫ₐᵇ f(x, t) dt = ∫ₐᵇ ∂f/∂x (x, t) dt", note: "Needs f and ∂f/∂x continuous" },
      { name: "General Leibniz Rule", formula: "d/dx ∫_(a(x))^(b(x)) f(x, t) dt = f(x, b) b′ − f(x, a) a′ + ∫ₐᵇ ∂f/∂x dt", note: "Moving limits add boundary terms" },
      { name: "Feynman's Trick", formula: "I(α) = ∫₀^∞ e^(−t) sin(αt)/t dt ⟹ I′(α) = 1/(1 + α²)", note: "So I(α) = arctan α" },
      { name: "Classic Result", formula: "∫₀^∞ sin x / x dx = π/2", note: "Found by differentiating with respect to a parameter" },
    ],
  },
  "fourier-transforms": {
    title: "Fourier Transform & Convolution",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      { name: "Transform Pair", formula: "F(ω) = ∫ f(x) e^(−iωx) dx,  f(x) = (1/2π) ∫ F(ω) e^(iωx) dω", note: "Conventions for 2π vary between books" },
      { name: "Derivative Rule", formula: "F{f′} = iω F(ω)", note: "Differentiation becomes multiplication" },
      { name: "Convolution Theorem", formula: "F{f * g} = F(ω) G(ω)", note: "Filtering in time is multiplying in frequency" },
      { name: "Gaussian", formula: "F{e^(−x²/2)} = √(2π) e^(−ω²/2)", note: "A Gaussian transforms into a Gaussian" },
      { name: "Plancherel", formula: "∫ |f|² dx = (1/2π) ∫ |F|² dω", note: "Energy is the same in both domains" },
    ],
  },
  "z-transforms": {
    title: "Z-Transform & Difference Equations",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      { name: "Definition", formula: "X(z) = Σₙ₌₀^∞ x[n] z⁻ⁿ", note: "Discrete analogue of the Laplace transform" },
      { name: "Basic Pairs", formula: "Z{aⁿ} = z / (z − a),  Z{1} = z / (z − 1)", note: "Region of convergence |z| > |a|" },
      { name: "Shift Property", formula: "Z{x[n + 1]} = zX(z) − z x[0]", note: "Turns difference equations into algebra" },
      { name: "Final Value Theorem", formula: "lim_(n→∞) x[n] = lim_(z→1) (z − 1) X(z)", note: "If the limit exists" },
      { name: "Stability", formula: "All poles inside |z| < 1", note: "The discrete system's response decays" },
    ],
  },
  "intro-pdes": {
    title: "Introduction to PDEs (Heat & Wave)",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      { name: "Heat Equation", formula: "u_t = k u_xx", note: "Diffusion: sharp features smooth out" },
      { name: "Heat Solution on [0, L]", formula: "u = Σ bₙ e^(−k(nπ/L)²t) sin(nπx/L)", note: "Ends held at 0; bₙ from the initial profile" },
      { name: "Wave Equation", formula: "u_tt = c² u_xx", note: "Disturbances travel at speed c" },
      { name: "d'Alembert's Formula", formula: "u = ½[f(x − ct) + f(x + ct)] + (1/2c) ∫_(x−ct)^(x+ct) g(s) ds", note: "f = initial shape, g = initial velocity" },
      { name: "Laplace's Equation", formula: "u_xx + u_yy = 0", note: "Steady state of the heat equation" },
    ],
  },
  "orthogonal-polynomials": {
    title: "Orthogonal Polynomials",
    category: "Calculus & Geometry",
    color: "#0284c7",
    formulas: [
      { name: "Orthogonality", formula: "∫ₐᵇ pₘ(x) pₙ(x) w(x) dx = 0  (m ≠ n)", note: "w(x) is the weight function" },
      { name: "Legendre", formula: "P₀ = 1, P₁ = x, (n + 1)Pₙ₊₁ = (2n + 1)x Pₙ − n Pₙ₋₁", note: "On [−1, 1] with w = 1; ∫ Pₙ² = 2/(2n + 1)" },
      { name: "Chebyshev", formula: "Tₙ(cos θ) = cos nθ,  Tₙ₊₁ = 2x Tₙ − Tₙ₋₁", note: "w = 1/√(1 − x²); best for interpolation" },
      { name: "Hermite", formula: "Hₙ₊₁ = 2x Hₙ − 2n Hₙ₋₁", note: "On ℝ with w = e^(−x²)" },
      { name: "Rodrigues' Formula", formula: "Pₙ(x) = (1 / 2ⁿn!) dⁿ/dxⁿ (x² − 1)ⁿ", note: "Generates every Legendre polynomial" },
    ],
  },


  // ═══════════════════════════════════════════════════════════════════════════
  // 2. Multivariable Calculus
  // ═══════════════════════════════════════════════════════════════════════════
  "partial-derivatives": {
    title: "Partial Derivatives & Gradients",
    category: "Multivariable Calculus",
    color: "#0d9488",
    formulas: [
      {
        name: "Partial Derivatives (x & y)",
        formula: "∂f/∂x = lim_(h→0) [f(x+h,y) - f(x,y)]/h · ∂f/∂y = lim_(h→0) [f(x,y+h) - f(x,y)]/h",
        note: "Differentiate with respect to one variable while holding the other constant",
      },
      {
        name: "Clairaut's Theorem (Mixed Partials)",
        formula: "∂²f / (∂x ∂y) = ∂²f / (∂y ∂x)",
        note: "Holds whenever second partial derivatives are continuous on an open set",
      },
      {
        name: "Multivariable Chain Rule",
        formula: "dz/dt = (∂z/∂x)(dx/dt) + (∂z/∂y)(dy/dt)  for z = f(x(t), y(t))",
        note: "Sum of partial contributions along each path coordinate",
      },
      {
        name: "Gradient Vector (∇f)",
        formula: "∇f = ⟨∂f/∂x, ∂f/∂y, ∂f/∂z⟩ = f_x î + f_y ĵ + f_z k̂",
        note: "Points in direction of maximum rate of increase; magnitude is max rate",
      },
      {
        name: "Directional Derivative",
        formula: "D_u f(x,y) = ∇f(x,y) · û = |∇f| cos θ  where |û| = 1",
        note: "Rate of change of f in the direction of unit vector û",
      },
      {
        name: "Tangent Plane to Surface z = f(x,y)",
        formula: "z - z₀ = f_x(x₀,y₀)(x - x₀) + f_y(x₀,y₀)(y - y₀)",
        note: "Normal vector to surface is n = ⟨f_x, f_y, -1⟩",
      },
    ],
  },

  extrema: {
    title: "Extreme Values & Hessians",
    category: "Multivariable Calculus",
    color: "#0d9488",
    formulas: [
      {
        name: "Critical Points Condition",
        formula: "∇f(x₀, y₀) = ⟨0, 0⟩  (or ∇f does not exist)",
        note: "Simultaneously solve f_x = 0 and f_y = 0",
      },
      {
        name: "Second Derivative Test (Hessian Determinant)",
        formula: "D = f_xx f_yy - (f_xy)² = det [[f_xx, f_xy], [f_yx, f_yy]]",
        note: "Evaluated at critical point (x₀, y₀)",
      },
      {
        name: "Classification Rules",
        formula: "D > 0 & f_xx > 0 ⟹ Local Min · D > 0 & f_xx < 0 ⟹ Local Max · D < 0 ⟹ Saddle Point",
        note: "If D = 0, the test is inconclusive",
      },
      {
        name: "Global Extrema on Closed Sets",
        formula: "1) Find critical pts in interior; 2) Find extrema on boundary; 3) Compare f values",
        note: "Extreme Value Theorem guarantees absolute max and min exist on compact sets",
      },
    ],
  },

  "lagrange-multipliers": {
    title: "Lagrange Multipliers",
    category: "Multivariable Calculus",
    color: "#0d9488",
    formulas: [
      {
        name: "Single Constraint Optimization",
        formula: "∇f(x,y,z) = λ ∇g(x,y,z)  subject to  g(x,y,z) = k",
        note: "At constrained extrema, level surfaces of f and g are tangent",
      },
      {
        name: "Lagrangian System",
        formula: "f_x = λ g_x,  f_y = λ g_y,  f_z = λ g_z,  g(x,y,z) = k",
        note: "System of n+1 equations in n+1 unknowns (coordinates + multiplier λ)",
      },
      {
        name: "Two Constraints Optimization",
        formula: "∇f = λ ∇g + μ ∇h  subject to  g = k₁ and h = k₂",
        note: "Extremizes f along curve formed by intersection of two constraint surfaces",
      },
    ],
  },

  "multiple-integrals": {
    title: "Multiple Integrals",
    category: "Multivariable Calculus",
    color: "#0d9488",
    formulas: [
      {
        name: "Fubini's Theorem (Iterated Integrals)",
        formula: "∬_R f(x,y) dA = ∫_a^b ∫_c^d f(x,y) dy dx = ∫_c^d ∫_a^b f(x,y) dx dy",
        note: "Integration order can be swapped for continuous functions on rectangular domains",
      },
      {
        name: "Double Integrals in Polar Coordinates",
        formula: "∬_R f(x,y) dA = ∫_α^β ∫_a^b f(r cos θ, r sin θ) r dr dθ  (dA = r dr dθ)",
        note: "Do not forget the Jacobian area scaling factor r",
      },
      {
        name: "Cylindrical Coordinates",
        formula: "∭_E f(x,y,z) dV = ∭ f(r cos θ, r sin θ, z) r dz dr dθ  (dV = r dz dr dθ)",
        note: "Ideal for regions with rotational symmetry around the z-axis",
      },
      {
        name: "Spherical Coordinates",
        formula: "∭ f(x,y,z) dV = ∭ f(ρ,θ,φ) ρ² sin φ dρ dφ dθ  (dV = ρ² sin φ dρ dφ dθ)",
        note: "x = ρ sin φ cos θ, y = ρ sin φ sin θ, z = ρ cos φ; φ is angle from +z axis",
      },
      {
        name: "General Jacobian Transformation",
        formula: "∬_R f(x,y) dx dy = ∬_G f(x(u,v), y(u,v)) |∂(x,y)/∂(u,v)| du dv",
        note: "Jacobian J = (∂x/∂u)(∂y/∂v) - (∂x/∂v)(∂y/∂u)",
      },
    ],
  },

  "vector-calculus": {
    title: "Vector Fields & Integrals",
    category: "Multivariable Calculus",
    color: "#0d9488",
    formulas: [
      {
        name: "Scalar Line Integral",
        formula: "∫_C f(x,y,z) ds = ∫_a^b f(r(t)) |r'(t)| dt  where ds = |r'(t)| dt",
        note: "Computes wire mass, average height, or curve area",
      },
      {
        name: "Vector Line Integral (Work)",
        formula: "W = ∫_C F · dr = ∫_a^b F(r(t)) · r'(t) dt = ∫_C (P dx + Q dy + R dz)",
        note: "Work done by vector field F along parametrized path r(t)",
      },
      {
        name: "Fundamental Theorem of Line Integrals",
        formula: "∫_C ∇f · dr = f(r(b)) - f(r(a))",
        note: "Path independent: depends only on starting and ending points",
      },
      {
        name: "Conservative Field Test (in ℝ² & ℝ³)",
        formula: "In ℝ²: ∂Q/∂x = ∂P/∂y · In ℝ³: curl F = ∇ × F = 0",
        note: "Guarantees existence of scalar potential f such that F = ∇f on simply connected domains",
      },
    ],
  },

  "divergence-curl": {
    title: "Vector Operators (Div & Curl)",
    category: "Multivariable Calculus",
    color: "#0d9488",
    formulas: [
      {
        name: "Divergence (Flux Density)",
        formula: "div F = ∇ · F = ∂P/∂x + ∂Q/∂y + ∂R/∂z",
        note: "Scalar field measuring local expansion (>0 source) or compression (<0 sink)",
      },
      {
        name: "Curl (Vorticity Vector)",
        formula: "curl F = ∇ × F = ⟨∂R/∂y - ∂Q/∂z, ∂P/∂z - ∂R/∂x, ∂Q/∂x - ∂P/∂y⟩",
        note: "Vector field measuring axis and intensity of local fluid rotation",
      },
      {
        name: "Key Identity: div(curl F) = 0",
        formula: "∇ · (∇ × F) = 0",
        note: "The divergence of any curl field is identically zero (no magnetic monopoles)",
      },
      {
        name: "Key Identity: curl(grad f) = 0",
        formula: "∇ × (∇f) = 0",
        note: "Conservative gradient fields are always irrotational",
      },
      {
        name: "Laplacian Operator (Δ = ∇²)",
        formula: "∇²f = ∇ · ∇f = ∂²f/∂x² + ∂²f/∂y² + ∂²f/∂z²",
        note: "Governs Laplace equation (∇²f = 0), heat equation, and wave equation",
      },
    ],
  },

  "stokes-theorem": {
    title: "Stokes' & Divergence Theorems",
    category: "Multivariable Calculus",
    color: "#0d9488",
    formulas: [
      {
        name: "Green's Theorem in the Plane",
        formula: "∮_C (P dx + Q dy) = ∬_D (∂Q/∂x - ∂P/∂y) dA",
        note: "C is positively oriented (counterclockwise), piecewise smooth boundary of D",
      },
      {
        name: "Area via Green's Theorem",
        formula: "Area(D) = ∮_C x dy = -∮_C y dx = ½ ∮_C (x dy - y dx)",
        note: "Calculates enclosed domain area using boundary contour integral",
      },
      {
        name: "Stokes' Theorem",
        formula: "∮_C F · dr = ∬_S (∇ × F) · dS = ∬_S (∇ × F) · n̂ dS",
        note: "Circulation of F around boundary C equals flux of curl F through surface S",
      },
      {
        name: "Divergence Theorem (Gauss' Theorem)",
        formula: "∬_S F · dS = ∭_E (∇ · F) dV",
        note: "Outward flux through closed surface S equals volume integral of divergence over solid E",
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Multivariable Calculus — further modules
  // ═══════════════════════════════════════════════════════════════════════════
  "3d-geometry": {
    title: "3D Analytical Geometry & Quadric Surfaces",
    category: "Multivariable Calculus",
    color: "#0d9488",
    formulas: [
      { name: "Line in Space", formula: "r = r₀ + t v  ⟺  (x − x₀)/a = (y − y₀)/b = (z − z₀)/c", note: "v = ⟨a, b, c⟩ is the direction" },
      { name: "Plane", formula: "a(x − x₀) + b(y − y₀) + c(z − z₀) = 0", note: "n = ⟨a, b, c⟩ is the normal" },
      { name: "Point-to-Plane Distance", formula: "d = |ax₀ + by₀ + cz₀ + d| / √(a² + b² + c²)", note: "For the plane ax + by + cz + d = 0" },
      { name: "Ellipsoid & Hyperboloids", formula: "x²/a² + y²/b² ± z²/c² = 1,  x²/a² − y²/b² − z²/c² = 1", note: "+ ellipsoid, one minus: one sheet, two minuses: two sheets" },
      { name: "Paraboloids & Cone", formula: "z = x²/a² ± y²/b²,  z² = x²/a² + y²/b²", note: "Same signs: bowl; opposite signs: saddle" },
    ],
  },
  "coordinate-transformations": {
    title: "Coordinate Transformations & Jacobians",
    category: "Multivariable Calculus",
    color: "#0d9488",
    formulas: [
      { name: "Jacobian", formula: "J = ∂(x, y)/∂(u, v) = xᵤ y_v − x_v yᵤ", note: "Local area scaling factor" },
      { name: "Change of Variables", formula: "∬_R f dx dy = ∬_S f(x(u,v), y(u,v)) |J| du dv", note: "Use the absolute value of J" },
      { name: "Polar & Cylindrical", formula: "dA = r dr dθ,  dV = r dr dθ dz", note: "x = r cos θ, y = r sin θ" },
      { name: "Spherical", formula: "x = ρ sin φ cos θ, y = ρ sin φ sin θ, z = ρ cos φ,  dV = ρ² sin φ dρ dφ dθ", note: "φ measured from the positive z-axis" },
      { name: "Inverse Jacobian", formula: "∂(u, v)/∂(x, y) = 1 / [∂(x, y)/∂(u, v)]", note: "Handy when u, v are given in terms of x, y" },
    ],
  },
  "constrained-optimization": {
    title: "Constrained & Unconstrained Optimization",
    category: "Multivariable Calculus",
    color: "#0d9488",
    formulas: [
      { name: "Hessian Test (n variables)", formula: "∇f = 0;  H positive definite ⟹ min,  negative definite ⟹ max", note: "Indefinite H ⟹ saddle point" },
      { name: "Global Extrema on a Closed Set", formula: "Compare f at interior critical points and on the boundary", note: "Extreme Value Theorem guarantees both exist" },
      { name: "Gradient Descent", formula: "x_(k+1) = x_k − α ∇f(x_k)", note: "For quadratics converges if 0 < α < 2/λ_max" },
      { name: "KKT Conditions", formula: "∇f + Σ μᵢ∇gᵢ + Σ λⱼ∇hⱼ = 0,  gᵢ ≤ 0,  μᵢ ≥ 0,  μᵢgᵢ = 0", note: "Minimize f subject to gᵢ ≤ 0, hⱼ = 0" },
      { name: "Bordered Hessian (2 variables, 1 constraint)", formula: "det [[0, gₓ, g_y], [gₓ, Lₓₓ, Lₓ_y], [g_y, L_yₓ, L_yy]] > 0 ⟹ local max", note: "< 0 ⟹ local min, with L = f − λg" },
    ],
  },
  "vector-fields-approximation": {
    title: "Vector Fields & Approximation Theory",
    category: "Multivariable Calculus",
    color: "#0d9488",
    formulas: [
      { name: "Directional Derivative", formula: "D_u f = ∇f · u,  |u| = 1", note: "Maximum value |∇f| in the direction of ∇f" },
      { name: "Multivariable Taylor (2nd order)", formula: "f(a + h) ≈ f(a) + ∇f(a)·h + ½ hᵀ H(a) h", note: "H is the Hessian matrix" },
      { name: "Implicit Function Theorem", formula: "F(x, y) = 0, F_y ≠ 0 ⟹ dy/dx = −Fₓ / F_y", note: "Three variables: ∂z/∂x = −Fₓ / F_z" },
      { name: "Vector Potential", formula: "∇ · B = 0 ⟹ B = ∇ × A", note: "On simply connected domains (no holes)" },
      { name: "Helmholtz Decomposition", formula: "F = −∇φ + ∇ × A", note: "Every smooth decaying field = curl-free + divergence-free" },
    ],
  },
  "potential-theory": {
    title: "Potential Theory",
    category: "Multivariable Calculus",
    color: "#0d9488",
    formulas: [
      { name: "Laplace & Poisson Equations", formula: "∇²u = 0,   ∇²u = −ρ/ε₀", note: "Harmonic functions solve Laplace's equation" },
      { name: "Mean Value Property", formula: "u(x₀) = (1 / area of sphere) ∯_(|x−x₀|=r) u dS", note: "Harmonic u equals its average on any sphere" },
      { name: "Fundamental Solutions", formula: "2D: (1/2π) ln r,   3D: −1/(4πr)", note: "∇² of these is the delta function" },
      { name: "Green's Identities", formula: "∭ (u∇²v − v∇²u) dV = ∯ (u ∂v/∂n − v ∂u/∂n) dS", note: "Second identity; basis for Green's functions" },
      { name: "Maximum Principle", formula: "max over D of u = max over ∂D of u", note: "No interior maxima or minima for harmonic u" },
    ],
  },
  "differential-forms": {
    title: "Differential Forms",
    category: "Multivariable Calculus",
    color: "#0d9488",
    formulas: [
      { name: "Wedge Product", formula: "dx ∧ dy = −dy ∧ dx,  dx ∧ dx = 0", note: "Antisymmetric product of 1-forms" },
      { name: "Exterior Derivative of a 1-form", formula: "d(P dx + Q dy) = (Qₓ − P_y) dx ∧ dy", note: "Green's theorem integrand" },
      { name: "d Squared Is Zero", formula: "d(dω) = 0", note: "Encodes curl(grad f) = 0 and div(curl F) = 0" },
      { name: "Generalized Stokes' Theorem", formula: "∫_∂M ω = ∫_M dω", note: "FTC, Green, Stokes and Gauss are all special cases" },
      { name: "Pullback", formula: "∫_(φ(D)) ω = ∫_D φ*ω", note: "Change of variables for forms" },
    ],
  },
  "advanced-integration": {
    title: "Advanced Integration & Variational Methods",
    category: "Multivariable Calculus",
    color: "#0d9488",
    formulas: [
      { name: "Gaussian Integral", formula: "∫₋∞^∞ e^(−x²) dx = √π", note: "Squared and evaluated in polar coordinates" },
      { name: "Functional", formula: "J[y] = ∫ₐᵇ F(x, y, y′) dx", note: "A number assigned to each function y" },
      { name: "Euler–Lagrange Equation", formula: "∂F/∂y − d/dx (∂F/∂y′) = 0", note: "Condition for J to be stationary" },
      { name: "Beltrami Identity", formula: "F − y′ ∂F/∂y′ = C  (F has no explicit x)", note: "Solves the brachistochrone quickly" },
      { name: "Constrained Variation", formula: "Stationary points of J[y] − λ K[y]", note: "Isoperimetric problems use a multiplier" },
    ],
  },
  "surface-geometry": {
    title: "Geometry of Surfaces",
    category: "Multivariable Calculus",
    color: "#0d9488",
    formulas: [
      { name: "First Fundamental Form", formula: "ds² = E du² + 2F du dv + G dv²,  E = r_u·r_u, F = r_u·r_v, G = r_v·r_v", note: "Measures lengths and angles on the surface" },
      { name: "Second Fundamental Form", formula: "L = r_uu·n,  M = r_uv·n,  N = r_vv·n", note: "Measures how the surface bends" },
      { name: "Gaussian Curvature", formula: "K = (LN − M²) / (EG − F²) = κ₁κ₂", note: "Sphere 1/R²; cylinder 0; saddle < 0" },
      { name: "Mean Curvature", formula: "H = (EN − 2FM + GL) / (2(EG − F²)) = (κ₁ + κ₂)/2", note: "Minimal surfaces have H = 0" },
      { name: "Gauss–Bonnet (closed surface)", formula: "∬_S K dA = 2π χ(S)", note: "Sphere: χ = 2, so ∬ K dA = 4π" },
    ],
  },
  "moving-domains": {
    title: "Integration on Moving Domains",
    category: "Multivariable Calculus",
    color: "#0d9488",
    formulas: [
      { name: "Leibniz Rule (1D)", formula: "d/dt ∫_(a(t))^(b(t)) f dx = ∫ₐᵇ f_t dx + f(b) b′ − f(a) a′", note: "Boundary terms come from moving limits" },
      { name: "Reynolds Transport Theorem", formula: "d/dt ∭_(V(t)) f dV = ∭ ∂f/∂t dV + ∯ f (v · n) dS", note: "Local change + flux through the moving boundary" },
      { name: "Material Derivative", formula: "Df/Dt = ∂f/∂t + v · ∇f", note: "Rate of change following a fluid particle" },
      { name: "Continuity Equation", formula: "∂ρ/∂t + ∇ · (ρv) = 0", note: "Conservation of mass" },
      { name: "Volume Change", formula: "d/dt |V(t)| = ∭ ∇ · v dV", note: "Divergence-free flow keeps volume fixed" },
    ],
  },
  "nonlinear-dynamics": {
    title: "Nonlinear Dynamical Systems",
    category: "Multivariable Calculus",
    color: "#0d9488",
    formulas: [
      { name: "Equilibria & Linearization", formula: "F(x*) = 0,  ẋ ≈ J(x*)(x − x*),  J = DF", note: "Hartman–Grobman: valid when no eigenvalue has Re λ = 0" },
      { name: "2D Classification", formula: "τ = tr J, Δ = det J: Δ < 0 saddle; Δ > 0, τ < 0 stable; τ > 0 unstable", note: "τ² < 4Δ gives spirals, τ² > 4Δ nodes" },
      { name: "Lyapunov Function", formula: "V > 0, V̇ = ∇V · F ≤ 0 ⟹ stable", note: "V̇ < 0 (except at x*) ⟹ asymptotically stable" },
      { name: "Conserved Quantity", formula: "dH/dt = 0 along trajectories", note: "Orbits lie on level sets of H" },
      { name: "Hopf Bifurcation", formula: "Eigenvalues λ = μ ± iω cross Re λ = 0", note: "A limit cycle is born" },
    ],
  },


  // ═══════════════════════════════════════════════════════════════════════════
  // 3. Linear Algebra
  // ═══════════════════════════════════════════════════════════════════════════
  "la-equations": {
    title: "Linear Equations & Systems",
    category: "Linear Algebra",
    color: "#2563eb",
    formulas: [
      {
        name: "General Linear System",
        formula: "a₁₁x₁ + a₁₂x₂ + ... + a₁ₙxₙ = b₁  ⟹  Ax = b",
        note: "A is m×n coefficient matrix, x is unknown vector, b is RHS vector",
      },
      {
        name: "Row Operations & RREF",
        formula: "1) Swap rows; 2) Scale row by k ≠ 0; 3) Add multiple of row to another",
        note: "Preserves solution set while reducing matrix to Reduced Row Echelon Form",
      },
      {
        name: "Rouché-Capelli Consistency Theorem",
        formula: "Consistent iff rank(A) = rank([A | b])",
        note: "Unique solution if rank = n; infinitely many if rank < n (n - rank free variables)",
      },
      {
        name: "Homogeneous System Ax = 0",
        formula: "Always has trivial solution x = 0; non-trivial solutions exist iff rank(A) < n",
        note: "For square A, rank(A) < n is equivalent to det(A) = 0; the solution space is Null(A)",
      },
    ],
  },

  "la-subspaces": {
    title: "Fundamental Subspaces & Rank-Nullity",
    category: "Linear Algebra",
    color: "#2563eb",
    formulas: [
      {
        name: "Rank-Nullity Theorem",
        formula: "rank(A) + nullity(A) = n  for A ∈ ℝ^(m×n)",
        note: "nullity(A) = dim Null(A) = n - rank(A)",
      },
      {
        name: "Dimensions of the Four Fundamental Subspaces",
        formula: "dim Col(A) = r · dim Row(A) = r · dim Null(A) = n-r · dim Null(Aᵀ) = m-r",
        note: "Here A is m×n and r = rank(A)",
      },
      {
        name: "Orthogonal Complement Pairs",
        formula: "Row(A)⊥ = Null(A)  ·  Col(A)⊥ = Null(Aᵀ)",
        note: "Null-space vectors are orthogonal to rows; left-null vectors are orthogonal to columns",
      },
      {
        name: "Column-Space Consistency Test",
        formula: "Ax = b is consistent ⇔ b ∈ Col(A) ⇔ rank(A) = rank([A | b])",
        note: "Equivalently, b must be orthogonal to every vector in Null(Aᵀ)",
      },
      {
        name: "General Solution of a Consistent System",
        formula: "x = x_p + v,  where v ∈ Null(A)",
        note: "If nullity(A)=k, the solution family has k independent free directions",
      },
      {
        name: "Injectivity / Independent Columns Test",
        formula: "Null(A) = {0} ⇔ nullity(A)=0 ⇔ rank(A)=n",
        note: "Equivalent to independent columns and a one-to-one linear map",
      },
      {
        name: "Subspace Test",
        formula: "0 ∈ W;  u,v ∈ W ⇒ u+v ∈ W;  c∈ℝ,u∈W ⇒ cu∈W",
        note: "A nonempty subset W is a subspace exactly when it is closed under linear combinations",
      },
      {
        name: "LU Factorization",
        formula: "A = LU  (or PA = LU with pivoting); solve Ly=b then Ux=y",
        note: "Factor once, then reuse the triangular solves for multiple right-hand sides",
      },
    ],
  },

  "la-vectors": {
    title: "Vectors, Span & Basis",
    category: "Linear Algebra",
    color: "#2563eb",
    formulas: [
      {
        name: "Linear Combination & Span",
        formula: "span{v₁, v₂, ..., v_k} = {c₁v₁ + c₂v₂ + ... + c_k v_k : c_i ∈ ℝ}",
        note: "Subspace containing all possible linear combinations of the vectors",
      },
      {
        name: "Linear Independence Test",
        formula: "c₁v₁ + c₂v₂ + ... + c_k v_k = 0 ⟹ c₁ = c₂ = ... = c_k = 0",
        note: "Vectors are linearly independent iff no vector is in the span of the others",
      },
      {
        name: "Basis & Dimension",
        formula: "Basis B = linearly independent set that spans subspace V; dim(V) = |B|",
        note: "Every basis of V has the exact same number of elements",
      },
      {
        name: "Dot Product & Norm",
        formula: "u · v = Σ u_i v_i = ||u|| ||v|| cos θ;  ||v|| = √(v · v)",
        note: "Vectors u, v are orthogonal iff u · v = 0",
      },
    ],
  },

  "la-matrices": {
    title: "Matrices, Inverses & Determinants",
    category: "Linear Algebra",
    color: "#2563eb",
    formulas: [
      {
        name: "Matrix Multiplication",
        formula: "(AB)_{ij} = Σ_{k=1}^n A_{ik} B_{kj}  (requires cols(A) = rows(B))",
        note: "In general AB ≠ BA (non-commutative); (AB)ᵀ = BᵀAᵀ",
      },
      {
        name: "2×2 Determinant & Inverse",
        formula: "det [[a, b], [c, d]] = ad - bc · A⁻¹ = [1/(ad - bc)] [[d, -b], [-c, a]]",
        note: "A is invertible (non-singular) if and only if det(A) ≠ 0",
      },
      {
        name: "Properties of Determinants",
        formula: "det(AB) = det(A)det(B) · det(Aᵀ) = det(A) · det(A⁻¹) = 1/det(A) · det(kA) = kⁿ det(A)",
        note: "Row swap flips determinant sign; scaling a row scales determinant by k",
      },
      {
        name: "Cramer's Rule",
        formula: "x_i = det(A_i(b)) / det(A)",
        note: "A_i(b) is matrix A with column i replaced by vector b",
      },
    ],
  },

  "la-transformations": {
    title: "Linear Transformations",
    category: "Linear Algebra",
    color: "#2563eb",
    formulas: [
      {
        name: "Linearity Axioms",
        formula: "T(u + v) = T(u) + T(v)  and  T(c u) = c T(u)  for all vectors u,v and scalar c",
        note: "Every linear transformation T: ℝⁿ → ℝᵐ can be represented by an m×n matrix",
      },
      {
        name: "Standard Matrix of T",
        formula: "A = [ T(e₁)  T(e₂)  ...  T(eₙ) ]",
        note: "Constructed by evaluating T on standard basis vectors e_i",
      },
      {
        name: "Kernel (Null Space) & Image (Range)",
        formula: "ker(T) = {x : T(x) = 0} · im(T) = {T(x) : x ∈ Domain}",
        note: "T is injective (one-to-one) iff ker(T) = {0}",
      },
      {
        name: "Rank-Nullity Theorem",
        formula: "dim(ker T) + dim(im T) = dim(Domain) = n",
        note: "Nullity(A) + Rank(A) = Number of columns (n)",
      },
    ],
  },

  "la-orthogonality": {
    title: "Orthogonality & Least Squares",
    category: "Linear Algebra",
    color: "#2563eb",
    formulas: [
      {
        name: "Inner Product and Induced Norm",
        formula: "⟨u,v⟩ = uᵀv (standard);  ||v|| = √⟨v,v⟩;  |⟨u,v⟩| ≤ ||u|| ||v||",
        note: "General inner products may be weighted, e.g. ⟨u,v⟩_W = uᵀWv for symmetric positive-definite W",
      },
      {
        name: "Orthogonal Matrix",
        formula: "QᵀQ = QQᵀ = I  ⟹  Q⁻¹ = Qᵀ;  ||Qx||₂ = ||x||₂;  det(Q) = ±1",
        note: "Rows and columns form orthonormal bases; lengths, angles, and dot products are preserved",
      },
      {
        name: "Orthogonal Projection onto Subspace W",
        formula: "proj_W(y) = (y·u₁/u₁·u₁) u₁ + ... + (y·u_k/u_k·u_k) u_k",
        note: "For an orthogonal basis {u₁, ..., u_k} of subspace W",
      },
      {
        name: "Gram-Schmidt Orthonormalization",
        formula: "v₁ = x₁;  v_k = x_k - Σ_{j=1}^{k-1} [(x_k · v_j)/(v_j · v_j)] v_j;  e_k = v_k / |v_k|",
        note: "Transforms any basis into an orthonormal basis",
      },
      {
        name: "QR Decomposition",
        formula: "A = QR  where Q has orthonormal columns (QᵀQ = I) and R is upper triangular",
        note: "Numerically stable foundation for least squares and eigenvalue algorithms",
      },
      {
        name: "Orthogonal Projection Matrix",
        formula: "P = A(AᵀA)⁻¹Aᵀ;  Pᵀ = P;  P² = P",
        note: "For full-column-rank A, P projects onto Col(A) and I-P projects onto Nul(Aᵀ)",
      },
      {
        name: "Normal Equations for Least Squares",
        formula: "AᵀA x̂ = Aᵀb;  if rank(A)=n, x̂ = (AᵀA)⁻¹ Aᵀb",
        note: "The residual b-Ax̂ is orthogonal to Col(A); the inverse formula requires full column rank",
      },
      {
        name: "QR Least Squares",
        formula: "A = QR  ⟹  R x̂ = Qᵀb",
        note: "For full-column-rank A, solve the triangular system instead of explicitly forming AᵀA",
      },
    ],
  },

  "la-eigen": {
    title: "Eigenvalues & Diagonalization",
    category: "Linear Algebra",
    color: "#2563eb",
    formulas: [
      {
        name: "Eigen-Equation",
        formula: "A v = λ v  with v ≠ 0  ⟹  (A - λ I) v = 0",
        note: "λ is eigenvalue; v is non-zero eigenvector in the null space of (A - λI)",
      },
      {
        name: "Characteristic Polynomial",
        formula: "p(λ) = det(A - λ I) = 0",
        note: "Roots of the n-th degree characteristic polynomial give the eigenvalues",
      },
      {
        name: "Algebraic vs Geometric Multiplicity",
        formula: "AM(λ) = root multiplicity;  GM(λ) = dim Nul(A - λI);  1 ≤ GM(λ) ≤ AM(λ)",
        note: "AM counts repeated roots; GM counts independent eigenvector directions for that eigenvalue",
      },
      {
        name: "Diagonalizability Criterion",
        formula: "A diagonalizable ⇔ Σ GM(λ) = n ⇔ GM(λ) = AM(λ) for every λ (when the characteristic polynomial splits)",
        note: "Distinct eigenvalues are sufficient; repeated eigenvalues require enough independent eigenvectors",
      },
      {
        name: "Matrix Diagonalization",
        formula: "A = P D P⁻¹  ⟹  Aᵏ = P Dᵏ P⁻¹",
        note: "P columns are linearly independent eigenvectors; D is diagonal matrix of eigenvalues",
      },
      {
        name: "Spectral Theorem for Symmetric Matrices",
        formula: "If A = Aᵀ ⟹ A is orthogonally diagonalizable: A = Q Λ Qᵀ  (Qᵀ = Q⁻¹)",
        note: "All eigenvalues of real symmetric matrices are real; eigenvectors can be chosen orthonormal",
      },
      {
        name: "Spectral Decomposition & Powers",
        formula: "A = Σ λ_i q_i q_iᵀ;  Aᵏ = Q Λᵏ Qᵀ = Σ λ_iᵏ q_i q_iᵀ",
        note: "For real symmetric A, q_i are orthonormal eigenvectors and q_i q_iᵀ are orthogonal projectors",
      },
      {
        name: "Trace and Determinant Invariants",
        formula: "tr(A) = Σ λ_i = Σ A_{ii}  and  det(A) = Π λ_i",
        note: "Sum of eigenvalues equals trace; product of eigenvalues equals determinant",
      },
    ],
  },

  "la-svd": {
    title: "Singular Value Decomposition (SVD)",
    category: "Linear Algebra",
    color: "#2563eb",
    formulas: [
      {
        name: "Full SVD Factorization",
        formula: "A = U Σ Vᵀ  for any m×n matrix A",
        note: "U is m×m orthogonal, V is n×n orthogonal, Σ is m×n diagonal with singular values σ₁ ≥ σ₂ ≥ ... ≥ 0",
      },
      {
        name: "Singular Values Definition",
        formula: "σ_i = √(λ_i(AᵀA))",
        note: "Square roots of the non-negative eigenvalues of the symmetric matrix AᵀA",
      },
      {
        name: "Compact Rank-r SVD",
        formula: "rank(A)=r  ⟹  A = U_r Σ_r V_rᵀ",
        note: "U_r is m×r, Σ_r is r×r with positive singular values, and V_r is n×r",
      },
      {
        name: "SVD and Fundamental Subspaces",
        formula: "Col(A)=span(u₁,…,u_r); Row(A)=span(v₁,…,v_r); Nul(A)=span(v_{r+1},…)",
        note: "Remaining left singular vectors span Nul(Aᵀ)",
      },
      {
        name: "Moore-Penrose Pseudoinverse",
        formula: "A⁺ = V Σ⁺ Uᵀ;  x⁺ = A⁺b",
        note: "Reciprocate nonzero singular values; x⁺ is the minimum-norm least-squares solution",
      },
      {
        name: "Eckart-Young-Mirsky Errors",
        formula: "A_k = Σ_{i=1}^k σ_i u_i v_iᵀ;  ||A-A_k||₂ = σ_{k+1};  ||A-A_k||_F = √(Σ_{i>k} σ_i²)",
        note: "Truncated SVD is optimal among rank-k matrices in both spectral and Frobenius norms",
      },
      {
        name: "2-Norm and Condition Number",
        formula: "||A||₂ = σ₁;  κ₂(A)=σ_max/σ_min for nonsingular square A",
        note: "If the smallest required singular value is zero, the matrix is singular and κ₂ is infinite",
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 4. Probability and Statistics
  // ═══════════════════════════════════════════════════════════════════════════
  "prob-basics": {
    title: "Probability Foundations & Bayes",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      {
        name: "Kolmogorov Probability Axioms",
        formula: "1) P(A) ≥ 0;  2) P(Ω) = 1;  3) P(∪ A_i) = Σ P(A_i) for disjoint events",
        note: "Axiomatic basis of mathematical probability theory",
      },
      {
        name: "Addition Rule & Complement",
        formula: "P(A ∪ B) = P(A) + P(B) - P(A ∩ B) · P(Aᶜ) = 1 - P(A)",
        note: "If A and B are mutually exclusive, P(A ∩ B) = 0",
      },
      {
        name: "Conditional Probability & Multiplication Rule",
        formula: "P(A | B) = P(A ∩ B) / P(B)  ⟹  P(A ∩ B) = P(B) P(A | B)",
        note: "Requires P(B) > 0",
      },
      {
        name: "Statistical Independence Test",
        formula: "A and B independent ⟺ P(A ∩ B) = P(A) P(B) ⟺ P(A | B) = P(A)",
        note: "Occurrence of event B provides zero information regarding event A",
      },
      {
        name: "Law of Total Probability",
        formula: "P(B) = Σ_{i=1}^n P(B | A_i) P(A_i)  for partition {A_1, ..., A_n}",
        note: "Denominator in Bayes' formula",
      },
      {
        name: "Bayes' Theorem",
        formula: "P(A_k | B) = [P(B | A_k) P(A_k)] / [Σ_{i=1}^n P(B | A_i) P(A_i)]",
        note: "Updates prior probability P(A) to posterior probability P(A|B) based on evidence B",
      },
    ],
  },

  "prob-random-vars": {
    title: "Random Variables & Expectations",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      {
        name: "Expected Value (Discrete & Continuous)",
        formula: "E[X] = μ = Σ x P(X = x)  [discrete]  or  ∫_{-∞}^∞ x f(x) dx  [continuous]",
        note: "Probability-weighted long-run average of random variable X",
      },
      {
        name: "Variance and Standard Deviation",
        formula: "Var(X) = σ² = E[(X - μ)²] = E[X²] - (E[X])² · σ = √(Var(X))",
        note: "Properties: Var(aX + b) = a² Var(X)",
      },
      {
        name: "Cumulative Distribution Function (CDF)",
        formula: "F(x) = P(X ≤ x) = ∫_{-∞}^x f(t) dt  ⟹  P(a < X ≤ b) = F(b) - F(a)",
        note: "f(x) = F'(x) for continuous random variables",
      },
      {
        name: "Linearity of Expectation",
        formula: "E[aX + bY + c] = a E[X] + b E[Y] + c",
        note: "Always holds, regardless of whether X and Y are independent",
      },
      {
        name: "Covariance & Correlation",
        formula: "Cov(X, Y) = E[(X - μ_X)(Y - μ_Y)] = E[XY] - E[X]E[Y] · ρ = Cov(X,Y) / (σ_X σ_Y)",
        note: "Measures linear association (-1 ≤ ρ ≤ 1). If independent ⟹ Cov(X,Y) = 0 and Var(X + Y) = Var(X) + Var(Y)",
      },
      {
        name: "Moment Generating Function (MGF)",
        formula: "M_X(t) = E[e^{tX}] · E[X^k] = M_X^{(k)}(0) = d^k M_X(t)/dt^k |_{t=0}",
        note: "Generates all raw moments via derivatives; for independent sum M_{X+Y}(t) = M_X(t) M_Y(t)",
      },
      {
        name: "Joint, Marginal & Conditional Densities",
        formula: "f_X(x) = ∫_{-∞}^∞ f(x,y) dy · f(y|x) = f(x,y)/f_X(x) · P((X,Y) ∈ R) = ∬_R f(x,y) dx dy",
        note: "Independence holds if and only if f(x,y) = f_X(x) f_Y(y) across the entire joint support",
      },
      {
        name: "Variance of Linear Combinations",
        formula: "Var(aX + bY) = a² Var(X) + b² Var(Y) + 2ab Cov(X, Y)",
        note: "Crucial for portfolio variance and sampling error of linear contrasts",
      },
    ],
  },

  "prob-distributions": {
    title: "Probability Distributions & Sampling Distributions",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      {
        name: "Binomial Distribution B(n, p)",
        formula: "P(X = k) = (n choose k) pᵏ (1-p)ⁿ⁻ᵏ · E[X] = np · Var(X) = np(1-p) · M_X(t) = (1 - p + pe^t)ⁿ",
        note: "Counts successes in n independent Bernoulli trials; converges to Poisson for small p, large n",
      },
      {
        name: "Poisson Distribution Pois(λ)",
        formula: "P(X = k) = (λᵏ e⁻λ) / k! · E[X] = λ · Var(X) = λ · M_X(t) = exp(λ(e^t - 1))",
        note: "Models count of rare events in continuous interval; equidispersed (mean = variance)",
      },
      {
        name: "Normal (Gaussian) Distribution N(μ, σ²)",
        formula: "f(x) = [1 / (σ√(2π))] exp(-½ [(x - μ)/σ]²) · Z = (X - μ)/σ ~ N(0, 1) · M_X(t) = exp(μt + ½σ²t²)",
        note: "Empirical rule: 68% in ±1σ, 95% in ±2σ, 99.7% in ±3σ; stable under linear combinations",
      },
      {
        name: "Exponential Distribution Exp(λ)",
        formula: "f(x) = λ e⁻λx (x ≥ 0) · F(x) = 1 - e⁻λx · E[X] = 1/λ · Var(X) = 1/λ² · M_X(t) = λ/(λ - t)",
        note: "Continuous waiting time; uniquely memoryless: P(X > s + t | X > s) = P(X > t)",
      },
      {
        name: "Student's t-Distribution",
        formula: "t = (x̄ - μ) / (s / √n) ~ t_{n-1} · Var(t) = ν / (ν - 2) for ν > 2",
        note: "Heavier tails than standard normal; accounts for extra uncertainty of estimating σ by s; approaches N(0,1) as n → ∞",
      },
      {
        name: "Chi-Square Distribution (χ²)",
        formula: "V = (n - 1)s² / σ² ~ χ²_{n-1} · E[V] = ν · Var(V) = 2ν",
        note: "Sum of ν squared independent standard normal variates; basis of variance testing and goodness-of-fit",
      },
      {
        name: "Fisher-Snedecor F-Distribution",
        formula: "F = (s₁² / σ₁²) / (s₂² / σ₂²) ~ F_{ν₁, ν₂}",
        note: "Ratio of two independent scaled chi-square variates; foundation of ANOVA and regression significance",
      },
    ],
  },

  "prob-descriptive": {
    title: "Descriptive Statistics & Point Estimation",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      {
        name: "Sample Mean & Trimmed Mean",
        formula: "x̄ = (1/n) Σ_{i=1}^n x_i · E[x̄] = μ [Unbiased]",
        note: "Arithmetic center; sensitive to extreme outlier values",
      },
      {
        name: "Sample Variance & Bessel's Correction",
        formula: "s² = [1 / (n - 1)] Σ_{i=1}^n (x_i - x̄)² · E[s²] = σ² [Unbiased] · s = √(s²)",
        note: "Bessel's correction (n - 1) compensates for sample mean estimation, eliminating negative bias",
      },
      {
        name: "Maximum Likelihood Estimation (MLE)",
        formula: "L(θ) = ∏ f(x_i; θ) ⟹ ℓ(θ) = ln L(θ) ⟹ dℓ(θ)/dθ = 0 [Score Equation]",
        note: "Asymptotically unbiased, efficient, and normally distributed: θ̂_MLE ~ N(θ, 1/I(θ))",
      },
      {
        name: "Method of Moments (MoM)",
        formula: "Sample Moment m_k = (1/n) Σ x_i^k = Theoretical Moment E[X^k; θ]",
        note: "Equates sample empirical moments to theoretical moments to solve for unknown parameters",
      },
      {
        name: "Estimator Bias & Mean Squared Error (MSE)",
        formula: "Bias(θ̂) = E[θ̂] - θ · MSE(θ̂) = E[(θ̂ - θ)²] = Var(θ̂) + [Bias(θ̂)]²",
        note: "Quantifies the fundamental bias-variance trade-off in point estimation",
      },
      {
        name: "Standard Score & Outlier Fences",
        formula: "z = (x - x̄) / s · IQR = Q₃ - Q₁ · Outliers: x < Q₁ - 1.5(IQR) or x > Q₃ + 1.5(IQR)",
        note: "Measures relative position; boxplot whiskers span to most extreme non-outlier data points",
      },
    ],
  },

  "prob-hypothesis": {
    title: "Hypothesis Testing & ANOVA",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      {
        name: "Central Limit Theorem (CLT)",
        formula: "For sample mean x̄ with n ≥ 30: x̄ ~ N(μ, σ²/n) ⟹ Z = (x̄ - μ) / (σ/√n)",
        note: "Sampling distribution of mean approaches normal regardless of population shape",
      },
      {
        name: "One-Sample & Two-Sample Tests",
        formula: "1-sample: t = (x̄ - μ₀)/(s/√n) · 2-sample pooled: t = (x̄₁ - x̄₂)/[s_p √(1/n₁ + 1/n₂)]",
        note: "Pooled variance s_p² = [(n₁-1)s₁² + (n₂-1)s₂²] / (n₁ + n₂ - 2) with df = n₁ + n₂ - 2",
      },
      {
        name: "Paired Differences t-Test",
        formula: "d_i = x_{1i} - x_{2i} ⟹ t = (d̄ - μ_{d0}) / (s_d / √n) with df = n - 1",
        note: "Controls for subject-to-subject heterogeneity in before-and-after repeated measures",
      },
      {
        name: "Type I & Type II Errors & Power",
        formula: "α = P(Reject H₀ | H₀ true) · β = P(Fail to reject H₀ | H₀ false) · Power = 1 - β",
        note: "Power increases with larger sample size n, larger effect size |μ - μ₀|, and larger α",
      },
      {
        name: "One-Way ANOVA F-Test",
        formula: "SST = SSB + SSW · F = MSB / MSW = [SSB / (k - 1)] / [SSW / (N - k)] ~ F_{k-1, N-k}",
        note: "Omnibus test for H₀: μ₁ = ... = μ_k without inflating family-wise Type I error rate",
      },
      {
        name: "Chi-Square Tests (Goodness-of-Fit & Independence)",
        formula: "χ² = Σ [(O_i - E_i)² / E_i] (df = k-1-p) · Contingency Table: E_{ij} = (R_i · C_j) / N (df = (r-1)(c-1))",
        note: "Requires all expected counts E ≥ 5; measures divergence between observed and expected frequencies",
      },
    ],
  },

  "prob-regression": {
    title: "Linear Regression & OLS Inference",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      {
        name: "Pearson Correlation Coefficient (r)",
        formula: "r = [Σ (x_i - x̄)(y_i - ȳ)] / [√(Σ(x_i - x̄)²) √(Σ(y_i - ȳ)²)] = SS_{xy} / √(SS_{xx} SS_{yy})",
        note: "-1 ≤ r ≤ 1; measures strength and direction of linear relationship between variables",
      },
      {
        name: "Ordinary Least Squares (OLS) Normal Equations",
        formula: "b₁ = SS_{xy} / SS_{xx} = r (s_y / s_x) · b₀ = ȳ - b₁ x̄",
        note: "Derived by minimizing Σ (y_i - b₀ - b₁ x_i)²; regression line always passes through (x̄, ȳ)",
      },
      {
        name: "Standard Error of Slope & t-Test",
        formula: "SE(b₁) = s_e / √SS_{xx} · t = (b₁ - 0) / SE(b₁) ~ t_{n-2} · s_e = √(SSE / (n - 2))",
        note: "Tests null hypothesis H₀: β₁ = 0 (no linear relationship) against two-tailed alternative",
      },
      {
        name: "Regression ANOVA & Determination (R²)",
        formula: "SST = SSR + SSE · R² = SSR / SST = 1 - (SSE / SST) = r² · F = MSR / MSE ~ F_{1, n-2}",
        note: "In simple linear regression, F = t² identically; R² gives fraction of total y variation explained by x",
      },
      {
        name: "Confidence Interval vs. Prediction Interval",
        formula: "Mean: ŷ₀ ± t_{α/2} s_e √(1/n + (x₀-x̄)²/SS_{xx}) · Indiv: ŷ₀ ± t_{α/2} s_e √(1 + 1/n + (x₀-x̄)²/SS_{xx})",
        note: "Prediction interval for an individual is always wider due to added individual variance σ²",
      },
    ],
  },
  // Dedicated decks for the six additional Linear Algebra modules.
  "la-lu-decomposition": {
    "title": "LU Decomposition",
    "category": "Linear Algebra",
    "color": "#2563eb",
    "formulas": [
      {
        "name": "LU factorization",
        "formula": "A = LU, with L lower triangular and U upper triangular.",
        "note": "In Doolittle form, the diagonal entries of L are 1."
      },
      {
        "name": "Pivoted LU",
        "formula": "PA = LU, where P records row interchanges.",
        "note": "Pivoting is used when a pivot is zero and improves numerical robustness."
      },
      {
        "name": "Solving with LU",
        "formula": "Solve Ly = Pb, then Ux = y.",
        "note": "For PA = LU; omit P when the factorization is A = LU."
      },
      {
        "name": "Reusing a factorization",
        "formula": "Factor once, then use triangular solves for each new right-hand side.",
        "note": "Dense factorization costs O(n³); each triangular-solve pair costs O(n²)."
      }
    ]
  },
  "la-cholesky-decomposition": {
    "title": "Cholesky Decomposition",
    "category": "Linear Algebra",
    "color": "#2563eb",
    "formulas": [
      {
        "name": "Cholesky condition",
        "formula": "A real symmetric positive-definite matrix has A = LLᵀ.",
        "note": "L is lower triangular with positive diagonal; the complex form is A = LL*."
      },
      {
        "name": "Cholesky diagonal update",
        "formula": "Lⱼⱼ = √(aⱼⱼ − Σₖ<ⱼ Lⱼₖ²).",
        "note": "Positive definiteness ensures a positive radicand in exact arithmetic."
      },
      {
        "name": "Cholesky off-diagonal update",
        "formula": "Lᵢⱼ = (aᵢⱼ − Σₖ<ⱼ LᵢₖLⱼₖ) / Lⱼⱼ, for i > j.",
        "note": "Compute columns in order after the diagonal entry is available."
      },
      {
        "name": "Cholesky determinant",
        "formula": "det(A) = (∏ᵢ Lᵢᵢ)².",
        "note": "Solve Ax = b using Ly = b followed by Lᵀx = y."
      }
    ]
  },
  "la-jordan-normal-form": {
    "title": "Jordan Normal Form",
    "category": "Linear Algebra",
    "color": "#2563eb",
    "formulas": [
      {
        "name": "Jordan block",
        "formula": "Jₖ(λ) has λ on the diagonal and 1 on the superdiagonal.",
        "note": "All other entries are zero."
      },
      {
        "name": "Generalized eigenvector chain",
        "formula": "(A − λI)v₁ = 0; (A − λI)vⱼ = vⱼ₋₁.",
        "note": "A chain of length k generates a Jordan block of size k."
      },
      {
        "name": "Counting Jordan blocks",
        "formula": "Number of blocks for λ = dim ker(A − λI).",
        "note": "Their total size equals the algebraic multiplicity of λ."
      },
      {
        "name": "Diagonalizability via Jordan form",
        "formula": "A is diagonalizable exactly when every Jordan block has size 1.",
        "note": "Jordan form exists over a field where the characteristic polynomial splits, such as ℂ."
      }
    ]
  },
  "la-matrix-norms-conditioning": {
    "title": "Vector & Matrix Norms, Condition Number",
    "category": "Linear Algebra",
    "color": "#2563eb",
    "formulas": [
      {
        "name": "Induced matrix norm",
        "formula": "‖A‖ = maxₓ≠₀ ‖Ax‖ / ‖x‖.",
        "note": "The vector norm determines the induced matrix norm."
      },
      {
        "name": "Common matrix norms",
        "formula": "‖A‖₁ = maximum absolute column sum; ‖A‖∞ = maximum absolute row sum.",
        "note": "The spectral norm ‖A‖₂ equals the largest singular value."
      },
      {
        "name": "Condition number",
        "formula": "κ(A) = ‖A‖ ‖A⁻¹‖ for an invertible square matrix.",
        "note": "κ₂(A) = σmax / σmin. Large values indicate sensitivity."
      },
      {
        "name": "Right-hand-side sensitivity",
        "formula": "‖δx‖/‖x‖ ≤ κ(A) ‖δb‖/‖b‖.",
        "note": "For Ax=b and A(x+δx)=b+δb, with invertible A and nonzero b."
      }
    ]
  },
  "la-complex-vector-spaces": {
    "title": "Complex Vector Spaces (Hermitian & Unitary Matrices)",
    "category": "Linear Algebra",
    "color": "#2563eb",
    "formulas": [
      {
        "name": "Complex inner product",
        "formula": "⟨x,y⟩ = x*y = Σᵢ conjugate(xᵢ)yᵢ.",
        "note": "This convention is conjugate-linear in the first argument."
      },
      {
        "name": "Hermitian matrix",
        "formula": "A* = A, where * is conjugate transpose.",
        "note": "Hermitian matrices have real eigenvalues and an orthonormal eigenbasis."
      },
      {
        "name": "Unitary matrix",
        "formula": "U*U = UU* = I.",
        "note": "U⁻¹ = U*; unitary transformations preserve inner products and Euclidean norms."
      },
      {
        "name": "Hermitian spectral theorem",
        "formula": "A = UΛU*, with U unitary and Λ real diagonal.",
        "note": "This extends orthogonal diagonalization of real symmetric matrices."
      }
    ]
  },
  "la-quadratic-forms-definiteness": {
    "title": "Quadratic Forms & Definiteness",
    "category": "Linear Algebra",
    "color": "#2563eb",
    "formulas": [
      {
        "name": "Quadratic form",
        "formula": "q(x) = xᵀAx.",
        "note": "For real x, only the symmetric part (A+Aᵀ)/2 contributes."
      },
      {
        "name": "Positive definiteness",
        "formula": "xᵀAx > 0 for every nonzero x.",
        "note": "For real symmetric A, this is equivalent to every eigenvalue being positive."
      },
      {
        "name": "Sylvester criterion",
        "formula": "A real symmetric matrix is positive definite iff all leading principal minors are positive.",
        "note": "For positive semidefiniteness, leading principal minors alone are insufficient."
      },
      {
        "name": "Indefinite quadratic form",
        "formula": "A real symmetric A is indefinite when it has both positive and negative eigenvalues.",
        "note": "Then q(x) takes both positive and negative values."
      }
    ]
  },
  "la-change-of-basis-similarity": {
    "title": "Change of Basis & Similarity Transformations",
    "category": "Linear Algebra",
    "color": "#2563eb",
    "formulas": [
      {
        "name": "Coordinate matrix",
        "formula": "x = P[x]B when the columns of P are the basis B in standard coordinates.",
        "note": "Consequently [x]B = P⁻¹x."
      },
      {
        "name": "Similarity transformation",
        "formula": "[T]B = P⁻¹AP.",
        "note": "A represents T in standard coordinates; columns of P form B."
      },
      {
        "name": "Similarity invariants",
        "formula": "Similar matrices share the characteristic polynomial, eigenvalues, trace and determinant.",
        "note": "They represent the same linear operator in different bases."
      },
      {
        "name": "Changing domain and codomain bases",
        "formula": "[T]C←B = Q⁻¹AP.",
        "note": "P contains the domain basis B; Q contains the codomain basis C."
      }
    ]
  },
  "la-affine-homogeneous": {
    "title": "Affine Transformations & Homogeneous Coordinates",
    "category": "Linear Algebra",
    "color": "#2563eb",
    "formulas": [
      {
        "name": "Affine map",
        "formula": "f(x) = Ax + b.",
        "note": "It is linear exactly when b = 0."
      },
      {
        "name": "Homogeneous matrix",
        "formula": "[x;1] ↦ [A b; 0 1][x;1].",
        "note": "The augmented coordinate lets one matrix represent both a linear map and translation."
      },
      {
        "name": "Affine composition",
        "formula": "(A₂,b₂) ∘ (A₁,b₁) = (A₂A₁, A₂b₁+b₂).",
        "note": "With column vectors, the rightmost transformation acts first."
      },
      {
        "name": "Affine inverse",
        "formula": "f⁻¹(y) = A⁻¹y − A⁻¹b.",
        "note": "The inverse exists iff A is invertible."
      }
    ]
  },
  "la-principal-component-analysis": {
    "title": "Principal Component Analysis (PCA)",
    "category": "Linear Algebra",
    "color": "#2563eb",
    "formulas": [
      {
        "name": "Centering PCA data",
        "formula": "Xc = X − 1μᵀ.",
        "note": "Rows are observations; μ contains the column means."
      },
      {
        "name": "Sample covariance for PCA",
        "formula": "S = XcᵀXc / (n−1).",
        "note": "Assumes n > 1 observations and centered columns."
      },
      {
        "name": "Principal directions",
        "formula": "Principal directions are covariance eigenvectors, ordered by decreasing eigenvalue.",
        "note": "Equivalently, use the right singular vectors of centered data."
      },
      {
        "name": "Explained variance ratio",
        "formula": "Variance captured by the first k PCs = (λ₁+⋯+λₖ)/(λ₁+⋯+λₚ).",
        "note": "Requires positive total variance; PCA maximizes retained variance among rank-k orthogonal projections."
      }
    ]
  },
  "la-markov-chains-steady-states": {
    "title": "Markov Chains & Steady States",
    "category": "Linear Algebra",
    "color": "#2563eb",
    "formulas": [
      {
        "name": "Row-stochastic transition matrix",
        "formula": "Pᵢⱼ ≥ 0 and ΣⱼPᵢⱼ = 1.",
        "note": "Pᵢⱼ is the probability of moving from state i to state j."
      },
      {
        "name": "Distribution evolution",
        "formula": "pₜ₊₁ = pₜP for row probability vectors.",
        "note": "After k steps, pₜ₊ₖ = pₜPᵏ."
      },
      {
        "name": "Stationary distribution",
        "formula": "πP = π, with πᵢ ≥ 0 and Σᵢπᵢ = 1.",
        "note": "For column-vector conventions the transition equation is transposed."
      },
      {
        "name": "Convergence in a finite chain",
        "formula": "Irreducible and aperiodic finite chains converge to a unique stationary distribution.",
        "note": "Irreducibility alone gives uniqueness, but periodicity can prevent convergence."
      }
    ]
  },
  "la-linear-programming-simplex": {
    "title": "Linear Programming (Simplex Method)",
    "category": "Linear Algebra",
    "color": "#2563eb",
    "formulas": [
      {
        "name": "Slack variables",
        "formula": "aᵀx ≤ b becomes aᵀx + s = b with s ≥ 0.",
        "note": "This converts an inequality to an equality."
      },
      {
        "name": "Basic feasible solution",
        "formula": "For m independent equality constraints, choose m independent basis columns and set nonbasic variables to zero.",
        "note": "The resulting basic variables must be nonnegative."
      },
      {
        "name": "Reduced cost for maximization",
        "formula": "rⱼ = cⱼ − cBᵀB⁻¹aⱼ.",
        "note": "With this convention, a positive reduced cost may enter; all nonbasic rⱼ ≤ 0 certify optimality of a feasible basis."
      },
      {
        "name": "Minimum-ratio test",
        "formula": "For d = B⁻¹aⱼ, take minᵢ:dᵢ>0 xBᵢ/dᵢ.",
        "note": "With an improving entering variable and no positive dᵢ, the objective is unbounded along that direction."
      }
    ]
  },
  "la-vector-space-applications": {
    "title": "Vector Space Applications in Graphics & ML",
    "category": "Linear Algebra",
    "color": "#2563eb",
    "formulas": [
      {
        "name": "Projection onto an orthonormal subspace",
        "formula": "proj(x) = QQᵀx.",
        "note": "Columns of Q form an orthonormal basis of the target subspace."
      },
      {
        "name": "2D rotation matrix",
        "formula": "R(θ) = [cosθ −sinθ; sinθ cosθ].",
        "note": "With column vectors, positive θ rotates counterclockwise."
      },
      {
        "name": "Linear feature map",
        "formula": "z = Wx maps input features into another vector space.",
        "note": "Adding b gives an affine layer z = Wx+b."
      },
      {
        "name": "Ridge regression",
        "formula": "w = (XᵀX+λI)⁻¹Xᵀy for λ > 0.",
        "note": "This minimizes ‖Xw−y‖²+λ‖w‖²; the displayed form penalizes every coordinate."
      }
    ]
  },
  "la-iterative-solvers": {
    "title": "Iterative Solvers (Jacobi, Gauss–Seidel, SOR)",
    "category": "Linear Algebra",
    "color": "#2563eb",
    "formulas": [
      {
        "name": "Jacobi iteration",
        "formula": "x⁽ᵏ⁺¹⁾ = D⁻¹(b − (L+U)x⁽ᵏ⁾).",
        "note": "Uses A=D+L+U with nonzero diagonal; all updates use the previous iterate."
      },
      {
        "name": "Gauss–Seidel iteration",
        "formula": "(D+L)x⁽ᵏ⁺¹⁾ = b − Ux⁽ᵏ⁾.",
        "note": "Uses each newly computed component immediately."
      },
      {
        "name": "Stationary-iteration convergence",
        "formula": "x⁽ᵏ⁺¹⁾ = Tx⁽ᵏ⁾+c converges for every initial guess iff ρ(T)<1.",
        "note": "ρ(T) is the spectral radius of the iteration matrix."
      },
      {
        "name": "SOR relaxation",
        "formula": "(D+ωL)x⁽ᵏ⁺¹⁾ = ωb + [(1−ω)D−ωU]x⁽ᵏ⁾.",
        "note": "For symmetric positive-definite A, SOR converges for 0<ω<2; ω=1 is Gauss–Seidel."
      }
    ]
  },
  "la-eigenvalue-algorithms": {
    "title": "Eigenvalue Algorithms (Power Iteration & QR)",
    "category": "Linear Algebra",
    "color": "#2563eb",
    "formulas": [
      {
        "name": "Power iteration",
        "formula": "y = Ax⁽ᵏ⁾; x⁽ᵏ⁺¹⁾ = y/‖y‖.",
        "note": "Dominant-eigenvector convergence needs a uniquely dominant eigenvalue and a suitable nonzero initial component."
      },
      {
        "name": "Rayleigh quotient",
        "formula": "ρ(x) = x*Ax / (x*x), for x ≠ 0.",
        "note": "It equals the eigenvalue when x is an eigenvector."
      },
      {
        "name": "QR eigenvalue iteration",
        "formula": "Aₖ = QₖRₖ; Aₖ₊₁ = RₖQₖ = Qₖ*AₖQₖ.",
        "note": "Each step is a similarity transformation and preserves eigenvalues."
      },
      {
        "name": "Eigenpair residual",
        "formula": "r = Av − λv.",
        "note": "A small residual verifies an approximate eigenpair; small residual alone need not imply small eigenvector error."
      }
    ]
  },
  "la-dual-spaces": {
    "title": "Dual Spaces & Linear Functionals",
    "category": "Linear Algebra",
    "color": "#2563eb",
    "formulas": [
      {
        "name": "Dual space",
        "formula": "V* is the vector space of linear maps from V to its scalar field.",
        "note": "For finite-dimensional V, dim V* = dim V."
      },
      {
        "name": "Dual basis",
        "formula": "εⁱ(vⱼ) = δᵢⱼ.",
        "note": "The dual functional εⁱ extracts the ith coordinate in the basis v₁,…,vₙ."
      },
      {
        "name": "Covector coordinate change",
        "formula": "If x = PxB and f(x)=ax, then aB = aP.",
        "note": "Here a and aB are row representations of the same linear functional."
      },
      {
        "name": "Annihilator dimension",
        "formula": "dim W⁰ = dim V − dim W.",
        "note": "W⁰ consists of all functionals in V* that vanish on W; V is finite-dimensional."
      }
    ]
  },
  "la-tensor-products": {
    "title": "Tensor Products & Kronecker Products",
    "category": "Linear Algebra",
    "color": "#2563eb",
    "formulas": [
      {
        "name": "Tensor-product dimension",
        "formula": "dim(V⊗W) = (dim V)(dim W).",
        "note": "For finite-dimensional spaces, basis tensors vᵢ⊗wⱼ form a basis."
      },
      {
        "name": "Kronecker product size",
        "formula": "If A is m×n and B is p×q, then A⊗B is mp×nq.",
        "note": "Each scalar entry aᵢⱼ is replaced by the block aᵢⱼB."
      },
      {
        "name": "Kronecker mixed-product identity",
        "formula": "(A⊗B)(C⊗D) = (AC)⊗(BD).",
        "note": "The ordinary products AC and BD must be dimensionally compatible."
      },
      {
        "name": "Vectorization identity",
        "formula": "vec(AXB) = (Bᵀ⊗A)vec(X).",
        "note": "Uses column-stacking vectorization and compatible matrix dimensions."
      }
    ]
  },
  "la-spectral-graph": {
    "title": "Spectral Graph Theory",
    "category": "Linear Algebra",
    "color": "#2563eb",
    "formulas": [
      {
        "name": "Graph Laplacian",
        "formula": "L = D − A.",
        "note": "For an undirected graph with nonnegative weights, D contains weighted degrees."
      },
      {
        "name": "Laplacian quadratic form",
        "formula": "xᵀLx = Σ{i,j}∈E wᵢⱼ(xᵢ−xⱼ)².",
        "note": "Each undirected edge is counted once; this proves positive semidefiniteness."
      },
      {
        "name": "Zero Laplacian eigenvalues",
        "formula": "Multiplicity of eigenvalue 0 equals the number of connected components.",
        "note": "Connectivity uses edges of positive weight."
      },
      {
        "name": "Algebraic connectivity",
        "formula": "For n≥2, the second-smallest Laplacian eigenvalue λ₂ is positive iff the graph is connected.",
        "note": "A corresponding eigenvector is called a Fiedler vector."
      }
    ]
  },
  "la-matrix-calculus": {
    "title": "Matrix Calculus",
    "category": "Linear Algebra",
    "color": "#2563eb",
    "formulas": [
      {
        "name": "Gradient of a quadratic form",
        "formula": "∇x(xᵀAx) = (A+Aᵀ)x.",
        "note": "For real symmetric A, this simplifies to 2Ax."
      },
      {
        "name": "Least-squares gradient",
        "formula": "∇x(½‖Ax−b‖²) = Aᵀ(Ax−b).",
        "note": "The Hessian is AᵀA."
      },
      {
        "name": "Trace gradient",
        "formula": "∇X tr(AᵀX) = A.",
        "note": "The gradient uses the Frobenius inner product."
      },
      {
        "name": "Log-determinant gradient",
        "formula": "∇X log det(X) = X⁻ᵀ.",
        "note": "For real invertible X with positive determinant; on the SPD cone it equals X⁻¹."
      }
    ]
  },
  
  "prob-entropy-mutual-information": {
    title: "Entropy and Mutual Information",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      {
        name: "Shannon Entropy",
        formula: "H(X) = −Σ p(x) log p(x) · 0 ≤ H(X) ≤ log n",
        note: "Base 2 gives bits, base e gives nats; convention 0 log 0 = 0; maximum log n only for the uniform distribution",
      },
      {
        name: "Joint Entropy & Chain Rule",
        formula: "H(X,Y) = H(X) + H(Y|X) · H(Y|X) = Σ p(x) H(Y|X=x)",
        note: "H(X,Y) = H(X) + H(Y) only when X and Y are independent; conditioning never increases entropy on average",
      },
      {
        name: "Mutual Information",
        formula: "I(X;Y) = Σ p(x,y) log[p(x,y)/(p(x)p(y))] = H(X) − H(X|Y) = H(X) + H(Y) − H(X,Y)",
        note: "Symmetric and nonnegative; I(X;Y) = 0 if and only if X and Y are independent (stronger than zero correlation)",
      },
      {
        name: "Data-Processing Inequality",
        formula: "X → Y → Z (Markov chain) ⟹ I(X;Z) ≤ I(X;Y)",
        note: "Post-processing a variable cannot create information about the source",
      },
    ],
  },

  "prob-cross-entropy-kl": {
    title: "Cross-Entropy and KL Divergence",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      {
        name: "KL Divergence",
        formula: "D(P‖Q) = Σ p(x) log[p(x)/q(x)]",
        note: "Finite only if supp(P) ⊆ supp(Q); not symmetric, so D(P‖Q) ≠ D(Q‖P) in general",
      },
      {
        name: "Cross-Entropy",
        formula: "H(P,Q) = −Σ p(x) log q(x) = H(P) + D(P‖Q)",
        note: "Expected log loss when data come from P but the model predicts Q; its minimum over Q is H(P)",
      },
      {
        name: "Gibbs' Inequality",
        formula: "D(P‖Q) ≥ 0, with equality if and only if P = Q",
        note: "Proved with Jensen's inequality for the concave logarithm",
      },
      {
        name: "KL to a Uniform Distribution",
        formula: "D(P‖U_n) = log n − H(P)",
        note: "Zero only when P itself is uniform on the n outcomes",
      },
      {
        name: "Mutual Information as KL",
        formula: "I(X;Y) = D( p(x,y) ‖ p(x)p(y) )",
        note: "Distance of the joint distribution from independence",
      },
      {
        name: "Cross-Entropy and Maximum Likelihood",
        formula: "H(P̂, Q_θ) = −(1/N) Σᵢ log Q_θ(xᵢ)",
        note: "Minimizing empirical cross-entropy over θ is the same as maximizing the likelihood of the data",
      },
    ],
  },

  "prob-censoring-kaplan-meier": {
    title: "Censoring and Kaplan-Meier Estimation",
    category: "Probability & Stats",
    color: "#0f766e",
    formulas: [
      {
        name: "Right-Censored Data",
        formula: "Y = min(T, C), δ = 1{T ≤ C}",
        note: "δ = 1 is an observed event; δ = 0 means only T > Y is known (censored, not an event)",
      },
      {
        name: "Kaplan-Meier Estimator",
        formula: "Ŝ(t) = Π_{t_j ≤ t} (1 − d_j / n_j)",
        note: "n_j = number with Y ≥ t_j (censored subjects at t_j stay in the risk set); Ŝ drops only at event times",
      },
      {
        name: "Median Survival",
        formula: "t_med = min{ t : Ŝ(t) ≤ 0.5 }",
        note: "If Ŝ never reaches 0.5 the median is not reached",
      },
      {
        name: "Greenwood Variance",
        formula: "Var(Ŝ(t)) = Ŝ(t)² Σ_{t_j ≤ t} d_j / (n_j (n_j − d_j))",
        note: "Assumes non-informative censoring; unstable when the risk set is small",
      },
    ],
  },

  "prob-hazard-survival-models": {
    title: "Hazard Functions and Survival Models",
    category: "Probability & Stats",
    color: "#0f766e",
    formulas: [
      {
        name: "Hazard and Cumulative Hazard",
        formula: "h(t) = f(t)/S(t) = −d ln S/dt · H(t) = ∫₀ᵗ h(u) du",
        note: "h is a rate per unit time, not a probability, so it can exceed 1",
      },
      {
        name: "Survival from Hazard",
        formula: "S(t) = exp(−H(t)) · f(t) = h(t) S(t) · E[T] = ∫₀^∞ S(t) dt",
        note: "Requires S(0) = 1",
      },
      {
        name: "Exponential Model",
        formula: "h = λ · S(t) = e^(−λt) · mean = 1/λ · median = ln 2/λ",
        note: "Memoryless: P(T > s + t | T > s) = P(T > t)",
      },
      {
        name: "Weibull Model",
        formula: "S(t) = exp(−(t/η)^k) · h(t) = (k/η)(t/η)^(k−1)",
        note: "k < 1 decreasing, k = 1 constant, k > 1 increasing hazard; mean η Γ(1 + 1/k), median η (ln 2)^(1/k)",
      },
      {
        name: "System Reliability",
        formula: "Series: R = Π R_i · Parallel: R = 1 − Π (1 − R_i)",
        note: "Assumes independent components; in series, exponential hazards add",
      },
    ],
  },

  "prob-sequential-probability-ratio": {
    title: "Sequential Probability Ratio Tests",
    category: "Probability & Stats",
    color: "#b45309",
    formulas: [
      {
        name: "Log Likelihood Ratio",
        formula: "ln Λₙ = Σ ln[ f(xᵢ; θ₁) / f(xᵢ; θ₀) ]",
        note: "Simple versus simple hypotheses; for Bernoulli: success adds ln(p₁/p₀), failure adds ln[(1−p₁)/(1−p₀)]",
      },
      {
        name: "Wald Boundaries",
        formula: "A = (1 − β)/α · B = β/(1 − α)",
        note: "Reject H₀ when ln Λₙ ≥ ln A; accept H₀ when ln Λₙ ≤ ln B; otherwise continue sampling",
      },
      {
        name: "Error Bounds",
        formula: "α' + β' ≤ α + β · α' ≤ α/(1 − β) · β' ≤ β/(1 − α)",
        note: "Nominal rates are approximate because the likelihood ratio can overshoot a boundary",
      },
      {
        name: "Approximate Expected Sample Size",
        formula: "E₁[N] ≈ [(1−β) ln A + β ln B] / E₁[z] · E₀[N] ≈ [α ln A + (1−α) ln B] / E₀[z]",
        note: "E₁[z] = D(P₁‖P₀) > 0 and E₀[z] = −D(P₀‖P₁) < 0",
      },
    ],
  },

  "prob-multiple-testing": {
    title: "Multiple Testing and False Discoveries",
    category: "Probability & Stats",
    color: "#b45309",
    formulas: [
      {
        name: "FWER and FDR",
        formula: "FWER = P(V ≥ 1) · FDR = E[ V / max(R, 1) ]",
        note: "V = false rejections, R = all rejections; with all nulls true FDR = FWER",
      },
      {
        name: "Bonferroni",
        formula: "reject if pᵢ ≤ α/m · adjusted p = min(1, m pᵢ)",
        note: "Controls the FWER under any dependence; conservative for large m",
      },
      {
        name: "Holm Step-Down",
        formula: "smallest k with p₍ₖ₎ > α/(m − k + 1); reject the first k − 1",
        note: "Controls the FWER under any dependence and is at least as powerful as Bonferroni",
      },
      {
        name: "Benjamini-Hochberg",
        formula: "largest k with p₍ₖ₎ ≤ kα/m; reject p₍₁₎ … p₍ₖ₎",
        note: "Controls the FDR at α·m₀/m for independent (or PRDS) p-values; does not control the FWER",
      },
    ],
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // Probability and Statistics — further modules
  // ═══════════════════════════════════════════════════════════════════════════
  "prob-bayesian-inference": {
    title: "Bayesian Inference",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      { name: "Posterior", formula: "p(θ | x) ∝ p(x | θ) p(θ)", note: "Posterior ∝ likelihood × prior" },
      { name: "Beta–Binomial", formula: "Beta(α, β) prior + k successes in n ⟹ Beta(α + k, β + n − k)", note: "Conjugate pair" },
      { name: "Normal–Normal (known σ²)", formula: "μ_post = (μ₀/τ₀² + n x̄/σ²) / (1/τ₀² + n/σ²)", note: "Precision-weighted average of prior and data" },
      { name: "Posterior Mean (Beta)", formula: "E[p | x] = (α + k) / (α + β + n)", note: "Shrinks k/n toward the prior mean" },
      { name: "Credible Interval", formula: "P(a ≤ θ ≤ b | x) = 0.95", note: "A direct probability statement about θ" },
    ],
  },
  "prob-maximum-likelihood": {
    title: "Maximum Likelihood Estimation",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      { name: "Log-Likelihood", formula: "ℓ(θ) = Σᵢ log f(xᵢ | θ),  θ̂ = argmax ℓ(θ)", note: "Solve ℓ′(θ) = 0 and check it is a maximum" },
      { name: "Common MLEs", formula: "Bernoulli p̂ = x̄;  Poisson λ̂ = x̄;  Exponential λ̂ = 1/x̄", note: "Normal: μ̂ = x̄, σ̂² = (1/n) Σ(xᵢ − x̄)²" },
      { name: "Fisher Information", formula: "I(θ) = −E[∂²ℓ/∂θ²]", note: "Curvature of the log-likelihood" },
      { name: "Asymptotic Normality", formula: "θ̂ ≈ N(θ, 1 / (n I₁(θ)))", note: "Gives approximate standard errors" },
      { name: "Invariance", formula: "MLE of g(θ) is g(θ̂)", note: "e.g. MLE of σ is √σ̂²" },
    ],
  },
  "prob-confidence-intervals": {
    title: "Confidence Intervals",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      { name: "Mean, σ known", formula: "x̄ ± z_(α/2) σ/√n", note: "z₀.₀₂₅ = 1.96 for 95%" },
      { name: "Mean, σ unknown", formula: "x̄ ± t_(α/2, n−1) s/√n", note: "Use the t distribution with n − 1 df" },
      { name: "Proportion", formula: "p̂ ± z_(α/2) √(p̂(1 − p̂)/n)", note: "Needs np̂ and n(1 − p̂) ≥ 10" },
      { name: "Variance", formula: "[(n − 1)s² / χ²_(α/2),  (n − 1)s² / χ²_(1−α/2)]", note: "Normal data only" },
      { name: "Sample Size", formula: "n = (z_(α/2) σ / E)²", note: "E = desired margin of error; round up" },
    ],
  },
  "prob-mgf": {
    title: "Moment Generating Functions",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      { name: "Definition", formula: "M_X(t) = E[e^(tX)]", note: "Exists in an open interval around t = 0" },
      { name: "Moments", formula: "E[Xⁿ] = M_X⁽ⁿ⁾(0)", note: "Differentiate n times and set t = 0" },
      { name: "Sums of Independent Variables", formula: "M_(X+Y)(t) = M_X(t) M_Y(t)", note: "Identifies the distribution of a sum" },
      { name: "Common MGFs", formula: "Bin: (1 − p + peᵗ)ⁿ;  Pois: e^(λ(eᵗ−1));  N: e^(μt + σ²t²/2)", note: "Exponential(λ): λ/(λ − t), t < λ" },
      { name: "Uniqueness", formula: "M_X = M_Y near 0 ⟹ X and Y have the same distribution", note: "Basis of many proofs, including the CLT" },
    ],
  },
  "prob-chi-square": {
    title: "Chi-Square Tests",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      { name: "Test Statistic", formula: "χ² = Σ (O − E)² / E", note: "Large values mean poor agreement" },
      { name: "Goodness of Fit", formula: "df = k − 1 − (number of estimated parameters)", note: "k = number of categories" },
      { name: "Independence (r × c table)", formula: "Eᵢⱼ = (row total)(column total) / n,  df = (r − 1)(c − 1)", note: "Tests whether two categorical variables are related" },
      { name: "Validity Check", formula: "All expected counts Eᵢ ≥ 5", note: "Otherwise combine categories or use Fisher's exact test" },
      { name: "Effect Size", formula: "Cramér's V = √(χ² / (n · min(r − 1, c − 1)))", note: "0 = no association, 1 = perfect" },
    ],
  },
  "prob-nonparametric": {
    title: "Nonparametric Tests",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      { name: "Sign Test", formula: "S = #(xᵢ > m₀) ~ Bin(n, ½) under H₀", note: "Tests the median; drop values equal to m₀" },
      { name: "Wilcoxon Signed-Rank", formula: "W⁺ = Σ ranks of |dᵢ| with dᵢ > 0", note: "E[W⁺] = n(n + 1)/4 under H₀" },
      { name: "Mann–Whitney U", formula: "U = R₁ − n₁(n₁ + 1)/2", note: "Two independent samples; E[U] = n₁n₂/2" },
      { name: "Kruskal–Wallis", formula: "H = 12/(N(N + 1)) Σ Rⱼ²/nⱼ − 3(N + 1)", note: "Rank-based one-way ANOVA; H ≈ χ²(k − 1)" },
      { name: "Spearman's Rank Correlation", formula: "r_s = 1 − 6 Σ dᵢ² / (n(n² − 1))", note: "Pearson correlation of the ranks (no ties)" },
    ],
  },
  "prob-multiple-regression": {
    title: "Multiple Linear Regression",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      { name: "Model", formula: "y = Xβ + ε,  ε ~ N(0, σ²I)", note: "X includes a column of 1s for the intercept" },
      { name: "OLS Estimate", formula: "β̂ = (XᵀX)⁻¹ Xᵀy", note: "Minimizes the sum of squared residuals" },
      { name: "Coefficient Variance", formula: "Var(β̂) = σ²(XᵀX)⁻¹,  σ̂² = SSE / (n − p − 1)", note: "p = number of predictors" },
      { name: "Adjusted R²", formula: "R²_adj = 1 − (1 − R²)(n − 1)/(n − p − 1)", note: "Penalizes adding useless predictors" },
      { name: "Overall F-Test", formula: "F = (SSR/p) / (SSE/(n − p − 1))", note: "Tests whether any predictor matters" },
      { name: "Multicollinearity", formula: "VIFⱼ = 1 / (1 − Rⱼ²)", note: "VIF > 10 is a common warning sign" },
    ],
  },
  "prob-joint-distributions": {
    title: "Joint & Marginal Distributions",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      { name: "Marginals", formula: "f_X(x) = ∫ f(x, y) dy,  p_X(x) = Σ_y p(x, y)", note: "Integrate or sum out the other variable" },
      { name: "Conditional", formula: "f(y | x) = f(x, y) / f_X(x)", note: "Slice of the joint, renormalized" },
      { name: "Independence", formula: "f(x, y) = f_X(x) f_Y(y) for all x, y", note: "Joint factorizes into marginals" },
      { name: "Covariance", formula: "Cov(X, Y) = E[XY] − E[X]E[Y]", note: "ρ = Cov / (σ_X σ_Y)" },
      { name: "Variance of a Sum", formula: "Var(X + Y) = Var X + Var Y + 2 Cov(X, Y)", note: "Covariance term vanishes when independent" },
    ],
  },
  "prob-multivariate-normal": {
    title: "Multivariate Normal Distribution",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      { name: "Density", formula: "f(x) = (2π)^(−k/2) |Σ|^(−1/2) exp(−½ (x − μ)ᵀ Σ⁻¹ (x − μ))", note: "μ = mean vector, Σ = covariance matrix" },
      { name: "Linear Transformations", formula: "X ~ N(μ, Σ) ⟹ AX + b ~ N(Aμ + b, AΣAᵀ)", note: "Every linear combination is normal" },
      { name: "Conditional Distribution", formula: "X₁ | X₂ = x₂ ~ N(μ₁ + Σ₁₂Σ₂₂⁻¹(x₂ − μ₂), Σ₁₁ − Σ₁₂Σ₂₂⁻¹Σ₂₁)", note: "Basis of Gaussian regression" },
      { name: "Zero Covariance", formula: "Σ₁₂ = 0 ⟹ X₁ and X₂ independent", note: "True for jointly normal variables only" },
      { name: "Mahalanobis Distance", formula: "D² = (x − μ)ᵀ Σ⁻¹ (x − μ) ~ χ²(k)", note: "Used for outlier detection" },
    ],
  },
  "prob-stochastic-processes": {
    title: "Stochastic Processes & Markov Chains",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      { name: "Markov Property", formula: "P(Xₙ₊₁ | Xₙ, …, X₀) = P(Xₙ₊₁ | Xₙ)", note: "Future depends only on the present" },
      { name: "n-Step Transitions", formula: "P⁽ⁿ⁾ = Pⁿ  (Chapman–Kolmogorov)", note: "Rows of P sum to 1" },
      { name: "Stationary Distribution", formula: "πP = π,  Σ πᵢ = 1", note: "Irreducible aperiodic chains converge to π" },
      { name: "Poisson Process", formula: "N(t) ~ Poisson(λt),  inter-arrival times ~ Exp(λ)", note: "Independent increments" },
      { name: "Random Walk", formula: "Sₙ = X₁ + … + Xₙ,  E[Sₙ] = nμ,  Var(Sₙ) = nσ²", note: "Symmetric walk returns to 0 in 1D and 2D" },
    ],
  },
  "prob-clt": {
    title: "Central Limit Theorem",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      { name: "CLT", formula: "(X̄ₙ − μ) / (σ/√n) → N(0, 1)", note: "For i.i.d. data with finite variance" },
      { name: "Sum Form", formula: "Sₙ ≈ N(nμ, nσ²)", note: "Totals as well as averages" },
      { name: "Standard Error", formula: "SE(X̄) = σ/√n", note: "Quadruple n to halve the error" },
      { name: "Normal Approximation to Binomial", formula: "Bin(n, p) ≈ N(np, np(1 − p))", note: "Use continuity correction ±0.5" },
      { name: "Law of Large Numbers", formula: "X̄ₙ → μ as n → ∞", note: "Averages settle at the true mean" },
    ],
  },
  "prob-logistic-regression": {
    title: "Logistic Regression",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      { name: "Model", formula: "P(y = 1 | x) = 1 / (1 + e^(−(β₀ + βᵀx)))", note: "The sigmoid keeps outputs in (0, 1)" },
      { name: "Log-Odds", formula: "log(p / (1 − p)) = β₀ + βᵀx", note: "Linear on the logit scale" },
      { name: "Odds Ratio", formula: "OR = e^(βⱼ)", note: "Multiplicative change in odds per unit of xⱼ" },
      { name: "Log-Loss", formula: "−Σ [yᵢ log pᵢ + (1 − yᵢ) log(1 − pᵢ)]", note: "Minimized by maximum likelihood" },
      { name: "Gradient", formula: "∇ = Xᵀ(p − y)", note: "No closed form; solved iteratively" },
    ],
  },
  "prob-time-series": {
    title: "Time Series",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      { name: "Autocorrelation", formula: "ρ(k) = Cov(Xₜ, Xₜ₊ₖ) / Var(Xₜ)", note: "For a stationary series" },
      { name: "AR(1)", formula: "Xₜ = φXₜ₋₁ + εₜ,  stationary if |φ| < 1", note: "ρ(k) = φᵏ" },
      { name: "MA(1)", formula: "Xₜ = εₜ + θεₜ₋₁", note: "ρ(1) = θ/(1 + θ²), ρ(k) = 0 for k > 1" },
      { name: "Differencing", formula: "∇Xₜ = Xₜ − Xₜ₋₁", note: "Removes trends; the I in ARIMA" },
      { name: "Exponential Smoothing", formula: "ŝₜ = αxₜ + (1 − α)ŝₜ₋₁", note: "0 < α < 1; larger α reacts faster" },
    ],
  },
  "prob-monte-carlo": {
    title: "Monte Carlo Methods",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      { name: "Monte Carlo Integration", formula: "∫ₐᵇ g(x) dx ≈ (b − a)/N · Σ g(Uᵢ),  Uᵢ ~ U(a, b)", note: "Works in any dimension" },
      { name: "Error Rate", formula: "SE ≈ σ / √N", note: "10× accuracy needs 100× samples" },
      { name: "Inverse Transform Sampling", formula: "X = F⁻¹(U),  U ~ U(0, 1)", note: "Exponential: X = −ln(U)/λ" },
      { name: "Importance Sampling", formula: "E_p[g] = E_q[g p/q]", note: "Sample from q where g·p is large" },
      { name: "Acceptance–Rejection", formula: "Accept Y ~ q with probability f(Y) / (M q(Y))", note: "Needs f ≤ M q everywhere" },
    ],
  },
  "prob-bootstrap": {
    title: "Bootstrap Methods",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      { name: "Resampling", formula: "Draw x*₁, …, x*ₙ with replacement from the data; compute θ̂*", note: "Repeat B times (often 1,000–10,000)" },
      { name: "Bootstrap Standard Error", formula: "SE* = √(Σ (θ̂*_b − θ̄*)² / (B − 1))", note: "Spread of the bootstrap estimates" },
      { name: "Percentile Interval", formula: "[θ̂*_(α/2),  θ̂*_(1−α/2)]", note: "Quantiles of the bootstrap distribution" },
      { name: "Bias Estimate", formula: "bias* = θ̄* − θ̂", note: "Bias-corrected estimate: 2θ̂ − θ̄*" },
    ],
  },
  "prob-order-statistics": {
    title: "Order Statistics",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      { name: "Maximum", formula: "F_(X₍ₙ₎)(x) = [F(x)]ⁿ", note: "All n values must be ≤ x" },
      { name: "Minimum", formula: "F_(X₍₁₎)(x) = 1 − [1 − F(x)]ⁿ", note: "Min of exponentials is Exp(nλ)" },
      { name: "kth Order Statistic", formula: "f₍ₖ₎(x) = n!/((k − 1)!(n − k)!) F^(k−1) (1 − F)^(n−k) f(x)", note: "k − 1 below, n − k above" },
      { name: "Uniform Case", formula: "U₍ₖ₎ ~ Beta(k, n − k + 1),  E[U₍ₖ₎] = k/(n + 1)", note: "Order statistics of U(0, 1)" },
      { name: "Sample Range", formula: "R = X₍ₙ₎ − X₍₁₎", note: "Simple spread measure" },
    ],
  },
  "prob-queueing": {
    title: "Queueing Theory",
    category: "Probability & Stats",
    color: "#7c3aed",
    formulas: [
      { name: "Utilization", formula: "ρ = λ / μ  (stable if ρ < 1)", note: "λ = arrival rate, μ = service rate" },
      { name: "Little's Law", formula: "L = λW,  L_q = λW_q", note: "Holds for almost any queue" },
      { name: "M/M/1 Averages", formula: "L = ρ/(1 − ρ),  W = 1/(μ − λ)", note: "W_q = ρ/(μ − λ)" },
      { name: "M/M/1 Distribution", formula: "P(n in system) = (1 − ρ) ρⁿ", note: "Geometric number in system" },
      { name: "M/M/c Utilization", formula: "ρ = λ / (cμ)", note: "Use the Erlang C formula for waiting probability" },
    ],
  },
};

export default formulaData;